import * as _ from 'es-toolkit';
import store from '../redux';
import iso639 from 'iso639';
import moment from 'moment';
import '../locales/momentLocales';
import { patchMomentFrChOrdinal } from '../locales/patchMomentFrChOrdinal';
import striptags from 'striptags';
import {
    resolveEventTranslatedTitle,
    resolveEventTitleDisplay,
    eventTitleExportPair,
} from './eventTitleDisplay';
import { phonePresentation } from './phonePresentation';
import { push_messages_with_back as navigate_to_messages_compose } from './messageComposeNavigation';
import {
    EVENT_TYPE_PAUSE,
    is_programmation_pause,
} from './programmationPauses';
import { parseProgrammationTimeToken } from '../components/HoursField/normalizeHourInput';

patchMomentFrChOrdinal();

class Utils { }

Utils.CUSTOMER_TYPE_CINEMA            = 'cinema';
Utils.CUSTOMER_TYPE_CINEMA_FESTIVAL   = 'cinemafestival';
Utils.CUSTOMER_TYPE_MUSIC             = 'music';
Utils.CUSTOMER_TYPE_MUSIC_FESTIVAL    = 'musicfestival';
Utils.CUSTOMER_TYPE_THEATER           = 'theater';
Utils.CUSTOMER_TYPE_RECREATION_CENTER = 'recreationcenter';
Utils.CUSTOMER_TYPE_RECREATION_PARC   = 'recreationparc';
Utils.CUSTOMER_TYPE_SPORT             = 'sport';
Utils.CUSTOMER_TYPE_MUSEUM            = 'museum';
Utils.CUSTOMER_TYPE_GENERIC           = 'generic';

Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR    = 'distributor';
Utils.DISTRIBUTOR_TYPE_COMPANY        = 'company';
Utils.DISTRIBUTOR_TYPE_TOURING_AGENT  = 'touring_agent';

/** Default analyses/stats preset « today » when no saved prefs (excludes cinema-festival). */
Utils.customerTypeStatsDefaultsToToday = function (type) {
    return type === Utils.CUSTOMER_TYPE_CINEMA || type === Utils.CUSTOMER_TYPE_RECREATION_CENTER;
};

Utils.isCinemaCustomerType = function (type) {
    return type === Utils.CUSTOMER_TYPE_CINEMA || type === Utils.CUSTOMER_TYPE_CINEMA_FESTIVAL;
};

Utils.isTheaterCustomerType = function (type) {
    return type === Utils.CUSTOMER_TYPE_THEATER;
};

Utils.isMusicCustomerType = function (type) {
    return type === Utils.CUSTOMER_TYPE_MUSIC || type === Utils.CUSTOMER_TYPE_MUSIC_FESTIVAL;
};

Utils.isRecreationCustomerType = function (type) {
    return type === Utils.CUSTOMER_TYPE_RECREATION_PARC
        || type === Utils.CUSTOMER_TYPE_RECREATION_CENTER;
};

/** Sidebar / admin directory for film distributors, theater companies, music booking agents. */
Utils.distributorsMenuVisibleForCustomerType = function (type) {
    return !Utils.isRecreationCustomerType(type);
};

Utils.defaultDistributorTypeForCustomer = function (type) {
    if (Utils.isTheaterCustomerType(type))
        return Utils.DISTRIBUTOR_TYPE_COMPANY;
    if (Utils.isMusicCustomerType(type))
        return Utils.DISTRIBUTOR_TYPE_TOURING_AGENT;
    return Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR;
};

/**
 * Forced distributor.type for this customer, or null when several types are allowed.
 * Theater → company; cinema → distributor; music → touring_agent; others → choice.
 */
Utils.lockedDistributorTypeForCustomer = function (type) {
    if (Utils.isTheaterCustomerType(type))
        return Utils.DISTRIBUTOR_TYPE_COMPANY;
    if (Utils.isCinemaCustomerType(type))
        return Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR;
    if (Utils.isMusicCustomerType(type))
        return Utils.DISTRIBUTOR_TYPE_TOURING_AGENT;
    return null;
};

/**
 * Types shown in the partner form select (and admissible for this customer).
 * Locked customers get a single type; recreation never gets touring_agent;
 * others get distributor + company + touring_agent.
 */
Utils.allowedDistributorTypesForCustomer = function (type) {
    const locked = Utils.lockedDistributorTypeForCustomer(type);
    if (locked)
        return [locked];
    if (Utils.isRecreationCustomerType(type)) {
        return [
            Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR,
            Utils.DISTRIBUTOR_TYPE_COMPANY,
        ];
    }
    return [
        Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR,
        Utils.DISTRIBUTOR_TYPE_COMPANY,
        Utils.DISTRIBUTOR_TYPE_TOURING_AGENT,
    ];
};

Utils.distributorTypeLabelKey = function (partner_type) {
    if (partner_type === Utils.DISTRIBUTOR_TYPE_COMPANY)
        return 'distributors.type_company';
    if (partner_type === Utils.DISTRIBUTOR_TYPE_TOURING_AGENT)
        return 'distributors.type_touring_agent';
    return 'distributors.type_distributor';
};

Utils.filterDistributorsForCustomer = function (distributors, customerType) {
    const list = Array.isArray(distributors) ? distributors : [];
    const locked = Utils.lockedDistributorTypeForCustomer(customerType);
    if (!locked)
        return list;
    return list.filter(d => {
        const type = d?.type || Utils.DISTRIBUTOR_TYPE_DISTRIBUTOR;
        return type === locked;
    });
};

Utils.isFestivalCustomerType = function (type) {
    return type === Utils.CUSTOMER_TYPE_CINEMA_FESTIVAL || type === Utils.CUSTOMER_TYPE_MUSIC_FESTIVAL;
};

// Order modes a customer family may use. Festivals: loan + rent. Others: sell (buy) + rent.
Utils.equipmentOrderAllowedModes = function (customerType) {
    return Utils.isFestivalCustomerType(customerType) ? ['loan', 'rent'] : ['sell', 'rent'];
};

// Preferred default mode for a customer family (loan for festivals, sell otherwise).
Utils.equipmentOrderDefaultMode = function (customerType) {
    return Utils.isFestivalCustomerType(customerType) ? 'loan' : 'sell';
};

Utils.inferEquipmentOrderMode = function (orderItems, customerType) {
    const modes = [...new Set((orderItems || []).map(item => item?.mode).filter(Boolean))];
    if (modes.length === 0)
        return Utils.equipmentOrderDefaultMode(customerType);
    if (modes.length === 1)
        return modes[0];
    return 'both';
};

Utils.EQUIPMENT_CATALOG_CATEGORIES = [
    'cash_register',
    'payment_terminal',
    'entry_scanner',
    'card_printer',
    'kiosk',
    'network',
    'accessory',
    'consumable',
    'spare',
    'service',
];

// Display order is the array above (setup flow), not the translated label.
// Pass orderedCategories (from Kore category_orders) to override the default sequence.
Utils.sorted_equipment_catalog_categories = function (orderedCategories) {
    const defaults = [...Utils.EQUIPMENT_CATALOG_CATEGORIES];
    if (!Array.isArray(orderedCategories) || orderedCategories.length === 0)
        return defaults;

    const known = new Set(defaults);
    const result = [];
    orderedCategories.forEach(category => {
        if (known.has(category) && !result.includes(category))
            result.push(category);
    });
    defaults.forEach(category => {
        if (!result.includes(category))
            result.push(category);
    });
    return result;
};

Utils.format_equipment_order_items_summary = function (order_items, articles_by_id, lang) {
    return (order_items || [])
        .filter(item => (item.quantity || 0) > 0)
        .map(item => {
            const article = articles_by_id?.[item.article_id];
            let name = item.article_id;
            if (article?.name) {
                if (typeof article.name === 'string')
                    name = article.name;
                else
                    name = Utils.localized_or_fallback(article.name, lang, name);
            }
            return `${name} ×${item.quantity}`;
        })
        .join(', ');
};

Utils.SECTION_ADMIN     = 'admin';
Utils.SECTION_OPERATION = 'operation';
Utils.SECTION_ANALYSIS  = 'analysis';

Utils.MODULE_PLANNING       = 'planning';
Utils.MODULE_SHOP           = 'shop';
Utils.MODULE_RESOURCES      = 'resources';
Utils.MODULE_COMMUNICATIONS = 'communications';
Utils.MODULE_PUBLISHING     = 'publishing';
Utils.MODULE_TEAM           = 'team';
Utils.MODULE_WORKFLOWS      = 'workflows';

Utils.EVENT_TYPE_MOVIE       = "movie";
Utils.EVENT_TYPE_MUSIC_GROUP = "music_group";
Utils.EVENT_TYPE_EXHIBITION  = "exhibition";
Utils.EVENT_TYPE_DRAMA       = "drama";
Utils.EVENT_TYPE_SERVICE     = "service";
Utils.EVENT_TYPE_PAUSE       = EVENT_TYPE_PAUSE;

Utils.is_programmation_pause = is_programmation_pause;

Utils.parse_programmation_time_token = parseProgrammationTimeToken;

Utils.IMAGE_TYPE_DEFAULT          = "default";
Utils.IMAGE_TYPE_LOGO             = "logo";
Utils.IMAGE_TYPE_BANNIERE         = "banner";
Utils.IMAGE_TYPE_POSTER           = "poster";
Utils.IMAGE_TYPE_STILL            = "still";
Utils.IMAGE_TYPE_LOGO_A4          = 'logo-a4';
Utils.IMAGE_TYPE_LOGO_THERMAL     = 'logo-thermal';
Utils.IMAGE_TYPE_FAVICON          = 'favicon';
Utils.IMAGE_TYPE_APPICON          = 'appicon';
Utils.IMAGE_TYPE_PARTNERS         = 'partners';
Utils.IMAGE_TYPE_PARTNERS_A4      = 'partners-a4';
Utils.IMAGE_TYPE_PARTNERS_THERMAL = 'partners-thermal';

Utils.LINK_TYPE_CATEGORY  = "category";
Utils.LINK_TYPE_ARTICLE   = "article";
Utils.LINK_TYPE_URL       = "url";
Utils.LINK_TYPE_MOVIE     = "movie";
Utils.LINK_TYPE_SCREENING = "screening";

Utils.SERVICE_TYPE_GENERIC       = "service";
Utils.SERVICE_TYPE_KINDERCITY    = "kindercity";
Utils.SERVICE_TYPE_BABY_PARK     = "babypark";
Utils.SERVICE_TYPE_KIDS_PARK     = "kidspark";
Utils.SERVICE_TYPE_KIDS_ATELIER  = "kidsatelier";
Utils.SERVICE_TYPE_KIDS_CAMP     = "kidscamp";
Utils.SERVICE_TYPE_KIDS_GARDEN   = "kidsgarden";
Utils.SERVICE_TYPE_TEENS_STUDIO  = "teenstudio";
Utils.SERVICE_TYPE_TEENS_ATELIER = "teensatelier";
Utils.SERVICE_TYPE_TEENS_CAMP    = "teenscamp";

Utils.ACTIVITY_ACTOR       = "actor";
Utils.ACTIVITY_CAST        = "cast";
Utils.ACTIVITY_DIRECTOR    = "director";
Utils.ACTIVITY_EDITOR      = "editor";
Utils.ACTIVITY_PHOTOGRAPHY = "photography";
Utils.ACTIVITY_MUSIC       = "music";
Utils.ACTIVITY_PRODUCER    = "producer";
Utils.ACTIVITY_SCREENPLAY  = "screenplay";
Utils.ACTIVITY_STAGE_DIRECTION            = "stage_direction";
Utils.ACTIVITY_ASSISTANT_STAGE_DIRECTION  = "assistant_stage_direction";
Utils.ACTIVITY_DRAMATURGY                 = "dramaturgy";
Utils.ACTIVITY_OUTSIDE_EYE                = "outside_eye";
Utils.ACTIVITY_LIGHTING_DESIGN            = "lighting_design";
Utils.ACTIVITY_SOUND_DESIGN               = "sound_design";
Utils.ACTIVITY_VIDEO_DESIGN               = "video_design";
Utils.ACTIVITY_MUSICAL_CREATION           = "musical_creation";
Utils.ACTIVITY_SCENOGRAPHY                = "scenography";
Utils.ACTIVITY_SETS                       = "sets";
Utils.ACTIVITY_MAKEUP                     = "makeup";
Utils.ACTIVITY_COSTUMES                   = "costumes";
Utils.ACTIVITY_SOUND_OPERATION            = "sound_operation";
Utils.ACTIVITY_LIGHTING_OPERATION         = "lighting_operation";
Utils.ACTIVITY_ADMINISTRATION             = "administration";
Utils.ACTIVITY_PRODUCTION                 = "production";
Utils.ACTIVITY_TOURING                    = "touring";
Utils.ACTIVITY_CHOREOGRAPHY               = "choreography";
Utils.ACTIVITY_ASSISTANT_CHOREOGRAPHY     = "assistant_choreography";
Utils.ACTIVITY_CONDUCTOR                  = "conductor";
Utils.ACTIVITY_COMPOSER                   = "composer";
Utils.ACTIVITY_PROJECT_LEAD               = "project_lead";
Utils.ACTIVITY_MUSICIAN                   = "musician";
Utils.ACTIVITY_VOCALIST                   = "vocalist";
Utils.ACTIVITY_ARRANGER                   = "arranger";
Utils.ACTIVITY_ARTISTIC_DIRECTION         = "artistic_direction";
Utils.ACTIVITY_SOUND_ENGINEER             = "sound_engineer";
Utils.ACTIVITY_GUEST                      = "guest";
Utils.ACTIVITY_OTHER                      = "other";

Utils.PROGRAMMATION_SUPPORT_DCP        = "dcp";
Utils.PROGRAMMATION_SUPPORT_DVD        = "dvd";
Utils.PROGRAMMATION_SUPPORT_BLU_RAY    = "blu_ray";
Utils.PROGRAMMATION_SUPPORT_VIDEO_FILE = "video_file";
Utils.PROGRAMMATION_SUPPORT_OTHER      = "other";

Utils.MONDAY    = "mo";
Utils.TUESDAY   = "tu";
Utils.WEDNESDAY = "we";
Utils.THURSDAY  = "th";
Utils.FRIDAY    = "fr";
Utils.SATURDAY  = "sa";
Utils.SUNDAY    = "su";

Utils.MONDAY_FULL    = "monday";
Utils.TUESDAY_FULL   = "tuesday";
Utils.WEDNESDAY_FULL = "wednesday";
Utils.THURSDAY_FULL  = "thursday";
Utils.FRIDAY_FULL    = "friday";
Utils.SATURDAY_FULL  = "saturday";
Utils.SUNDAY_FULL    = "sunday";

Utils.TKTUSER_ROLE_ADMIN          = "admin";
Utils.TKTUSER_ROLE_POS            = "pos";
Utils.TKTUSER_ROLE_ON_SITE        = "on-site";
Utils.TKTUSER_ROLE_PARTNER        = "partner";
Utils.TKTUSER_ROLE_OVERBOOKER     = "overbooker";
Utils.TKTUSER_ROLE_TRANSFER       = "transfer";
Utils.TKTUSER_ROLE_ACCRED         = "accred";
Utils.TKTUSER_ROLE_TICKET_EDITOR  = "ticket_editor"
Utils.TKTUSER_ROLE_REFUND         = "refund";
Utils.TKTUSER_ROLE_WALLET_CASHIER = "wallet_cashier";
Utils.TKTUSER_ROLE_WALLET_MANAGER = "wallet_manager";
Utils.TKTUSER_ROLE_ACCOUNTING     = "accounting";
Utils.TKTUSER_ROLE_ENTRY_CHECK    = "entry_check";
Utils.TKTUSER_ROLE_STATS          = "stats";
Utils.TKTUSER_ROLE_CUSTOMER       = "customer";
Utils.TKTUSER_ROLE_ESHOP          = "eshop";
Utils.TKTUSER_ROLE_INTEGRATOR     = "integrator";
Utils.TKTUSER_ROLE_MOBILE         = "mobile";
Utils.TKTUSER_ROLE_PIHYVE         = "pihyve";

Utils.PRICING_ROLE_ESHOP    = "eshop";
Utils.PRICING_ROLE_ON_SITE  = "on-site";
Utils.PRICING_ROLE_PARTNER  = "partner";
Utils.PRICING_ROLE_ACCRED   = "accred";
Utils.PRICING_ROLE_ADMIN    = "admin";
Utils.PRICING_ROLE_MOBILE   = "mobile";
Utils.PRICING_ROLE_CUSTOMER = "customer";

Utils.PERIOD_DAY         = "day";
Utils.PERIOD_WEEK        = "week";
Utils.PERIOD_CINEMA_WEEK = "cinema_week";
Utils.PERIOD_MONTH       = "month";
Utils.PERIOD_YEAR        = "year";

Utils.DURATION_5_MIN     = 'PT5M';
Utils.DURATION_10_MIN    = 'PT10M';
Utils.DURATION_15_MIN    = 'PT15M';
Utils.DURATION_30_MIN    = 'PT30M';
Utils.DURATION_45_MIN    = 'PT45M';
Utils.DURATION_1_HOUR    = 'PT1H';
Utils.DURATION_6_HOURS   = 'PT6H';
Utils.DURATION_12_HOURS  = 'PT12H';
Utils.DURATION_1_DAY     = 'P1D';
Utils.DURATION_7_DAYS    = 'P7D';
Utils.DURATION_1_MONTH   = 'P1M';
Utils.DURATION_6_MONTHS  = 'P6M';
Utils.DURATION_1_YEAR    = 'P1Y';
Utils.DURATION_18_MONTHS = 'P1Y6M';
Utils.DURATION_2_YEARS   = 'P2Y';
Utils.DURATION_5_YEARS   = 'P5Y';

Utils.APPLICATION_STATUS_SUBMITTED = 'submitted';
Utils.APPLICATION_STATUS_ACCEPTED  = 'accepted';
Utils.APPLICATION_STATUS_REJECTED  = 'rejected';

Utils.TICKET_STATUS_NEW       = 'new';
Utils.TICKET_STATUS_ACTIVATED = 'activated';
Utils.TICKET_STATUS_PENDING   = 'pending';
Utils.TICKET_STATUS_BLOCKED   = 'blocked';

Utils.TASK_STATUS_NEW       = 'new';
Utils.TASK_STATUS_FREE      = 'free';
Utils.TASK_STATUS_ASSIGNED  = 'assigned';
Utils.TASK_STATUS_CLAIMED   = 'claimed';
Utils.TASK_STATUS_RENOUNCED = 'renounced';

Utils.read_task_shift_id = (task) => task?.shift_id ?? task?.shift?.id ?? task?.shift?._id ?? null;

Utils.plan_shift_path = (shift_id) => {
    if (!shift_id) {
        return '/plan';
    }
    return `/plan/shift/${encodeURIComponent(shift_id)}`;
};

Utils.ACTIVITY_NOTIFICATION_NONE  = 'none';
Utils.ACTIVITY_NOTIFICATION_PT10M = 'PT10M';
Utils.ACTIVITY_NOTIFICATION_PT30M = 'PT30M';
Utils.ACTIVITY_NOTIFICATION_PT1H  = 'PT1H';
Utils.ACTIVITY_NOTIFICATION_PT2H  = 'PT2H';
Utils.ACTIVITY_NOTIFICATION_PT6H  = 'PT6H';
Utils.ACTIVITY_NOTIFICATION_P1D   = 'P1D';
Utils.ACTIVITY_NOTIFICATION_P2D   = 'P2D';
Utils.ACTIVITY_NOTIFICATION_P3D   = 'P3D';

Utils.DEFAULT_LANGS = ['fr', 'en', 'de', 'it'];
Utils.LOCALES       = ['fr_CH', 'en_GB', 'de_CH', 'it_CH'];

Utils.POS_LAYOUT_TICKETS  = 'tickets';
Utils.POS_LAYOUT_ARTICLES = 'articles';

Utils.POS_PAYMENT_METHOD_CASH           = 'cash';
Utils.POS_PAYMENT_METHOD_CASH_COLLECTOR = 'cash_collector';
Utils.POS_PAYMENT_METHOD_EWALLET        = 'ewallet';
Utils.POS_PAYMENT_METHOD_SUMUP          = 'sumup';
Utils.POS_PAYMENT_METHOD_ZVT_EFT        = 'zvt_eft';
Utils.POS_PAYMENT_METHOD_OTHER_EFT      = 'other_eft';
Utils.POS_PAYMENT_METHOD_TWINT          = 'twint';
Utils.POS_PAYMENT_METHOD_PIMPAY         = 'pimpay';
Utils.POS_PAYMENT_METHOD_POS_TRANSFER   = 'pos_transfer';

Utils.ESHOP_PAYMENT_METHOD_FREE           = 'free';
Utils.ESHOP_PAYMENT_METHOD_PROXYPAY       = 'proxypay';
Utils.ESHOP_PAYMENT_METHOD_LATER          = 'later';
Utils.ESHOP_PAYMENT_METHOD_ESHOP_TRANSFER = 'eshop_transfer';

Utils.PAYMENT_METHOD_CASH           = 'POS_CASH';
Utils.PAYMENT_METHOD_CASH_COLLECTOR = 'POS_CASH_COLLECTOR';
Utils.PAYMENT_METHOD_EWALLET        = 'POS_WALLET';
Utils.PAYMENT_METHOD_SUMUP          = 'POS_SUMUP';
Utils.PAYMENT_METHOD_ZVT_EFT        = 'POS_ZVT_EFT';
Utils.PAYMENT_METHOD_OTHER_EFT      = 'POS_OTHER_EFT';
Utils.PAYMENT_METHOD_TWINT          = 'POS_TWINT';
Utils.PAYMENT_METHOD_PIMPAY         = 'POS_PIMPAY';
Utils.PAYMENT_METHOD_PROXYPAY       = 'PROXYPAY';
Utils.PAYMENT_METHOD_PROXYPAY_ALT   = 'PROXYPAY_ALT';
Utils.PAYMENT_METHOD_TRANSFER       = 'TRANSFER';
Utils.PAYMENT_METHOD_LATER          = 'LATER_PAYMENT';
Utils.PAYMENT_METHOD_FREE           = 'NULL_PAYMENT';

Utils.SHOW_CALCULATOR_OPTIONAL = 'optional';
Utils.SHOW_CALCULATOR_ALWAYS   = 'always';
Utils.SHOW_CALCULATOR_NEVER    = 'never';

/** Eshop cash register closing: default amount to withdraw from drawer (see cash_outflow_compute_mode). */
Utils.CASH_OUTFLOW_COMPUTE_MODE_AUTO = 'auto';
Utils.CASH_OUTFLOW_COMPUTE_MODE_ZERO = 'zero';
Utils.CASH_OUTFLOW_COMPUTE_MODE_NONE = 'none';

Utils.USER_FIELD_COMPANY      = 'company';
Utils.USER_FIELD_TITLE        = 'title';
Utils.USER_FIELD_FIRSTNAME    = 'firstname';
Utils.USER_FIELD_LASTNAME     = 'lastname';
Utils.USER_FIELD_EMAIL        = 'email';
Utils.USER_FIELD_ADDRESS      = 'address';
Utils.USER_FIELD_ZIP          = 'zip';
Utils.USER_FIELD_CITY         = 'city';
Utils.USER_FIELD_COUNTRY      = 'country';
Utils.USER_FIELD_AGE          = 'age';
Utils.USER_FIELD_BIRTHDATE    = 'birthdate';
Utils.USER_FIELD_PHOTO        = 'photo';
Utils.USER_FIELD_SEX          = 'sex';
Utils.USER_FIELD_PHONE        = 'phone';
Utils.USER_FIELD_CELLPHONE    = 'cellphone';
Utils.USER_FIELD_GIFT_MESSAGE = 'gift_message';
Utils.USER_FIELD_TABLE_NUMBER = 'tab';
Utils.USER_FIELD_COMMENT      = 'comment';

Utils.CREDIT_WALLET_MODE_CREDIT             = 'credit';
Utils.CREDIT_WALLET_MODE_DEBIT              = 'debit';
Utils.CREDIT_WALLET_MODE_REACH              = 'reach';
Utils.CREDIT_WALLET_MODE_REACH_FROM_BALANCE = 'reach_from_balance';

Utils.SEX_NC = 'n/c';
Utils.SEX_F  = 'f';
Utils.SEX_M  = 'm';
Utils.SEX_O  = 'o';

Utils.ENV_TEST = 'test';
Utils.ENV_PROD = 'prod';

Utils.REGISTRATION_CALLBACK_MODE_EMAIL     = 'email';
Utils.REGISTRATION_CALLBACK_MODE_CALLBACK  = 'callback';
Utils.REGISTRATION_CALLBACK_MODE_URL       = 'url';
Utils.REGISTRATION_CALLBACK_MODE_MAILCHIMP = 'mailchimp';
Utils.REGISTRATION_CALLBACK_MODE_BREVO     = 'brevo';
Utils.REGISTRATION_CALLBACK_MODE_GETRESPONSE = 'getresponse';

Utils.DONATION_MODE_CHECKOUT = 'checkout';
Utils.DONATION_MODE_HEADER   = 'header';
Utils.DONATION_MODE_FOOTER   = 'footer';
Utils.DONATION_MODE_EMAIL    = 'email';

Utils.TWINT_METHOD_STICKER = 'sticker';
Utils.TWINT_METHOD_BEACON  = 'beacon';

Utils.DELIVERY_TYPE_ONSITE   = 'onsite';
Utils.DELIVERY_TYPE_TAKEAWAY = 'takeaway';
Utils.DELIVERY_TYPE_PICKUP   = 'pickup';
Utils.DELIVERY_TYPE_SHIPMENT = 'shipment';
Utils.DELIVERY_TYPE_VIRTUAL  = 'virtual';

Utils.BOOKING_MODE_FREE  = 'free';
Utils.BOOKING_MODE_OTHER = 'other';
Utils.BOOKING_MODE_EMAIL = 'email';

Utils.PRINTER_PROFILES = [
    2,   //  => 00000010  Boca thermal (black mark on back)
    10,  //  => 00001010  Thermal printer (type ZDxx or ZX) with hole paper without cutter
    3,   //  => 00000011  Thermal printer (type ZDxx or ZX) with continuous paper without cutter
    18,  //  => 00010010  Thermal printer (type ZDxx or ZX) with continuous paper and cutter (cut after each ticket)
    26,  //  => 00011010  Thermal printer (type ZDxx or ZX) with hole paper and cutter (cut after each ticket)
    50,  //  => 00110010  Thermal printer (type ZDxx or ZX) with continuous paper and cutter (cut after the whole document)
    58,  //  => 00111010  Thermal printer (type ZDxx or ZX) with hole paper and cutter (cut after the whole document)
    1,   //  => 00000001  Card printer (type ZCxxx) with Black Ribbon and Duplex disabled
    65,  //  => 01000001  Card printer (type ZCxxx) with Color Ribbon and Duplex disabled
    129, //  => 10000001  Card printer (type ZCxxx) with Black Ribbon and Duplex enabled
    193, //  => 11000001  Card printer (type ZCxxx) with Color Ribbon and Duplex enabled
    4,   //  => 00000100  Other standard printer
    6,   //  => 00000110  Other thermal printer
];

Utils.PRINTER_PROFILES_BY_FAMILY = {
    boca: [2],
    zebraThermal: [10, 3, 18, 26, 50, 58],
    zebraCard: [1, 65, 129, 193],
    other: [10, 3, 18, 26, 50, 58, 1, 65, 129, 193, 4, 6],
};

Utils.getPrinterProfiles = (family) => {
    if (family && Utils.PRINTER_PROFILES_BY_FAMILY[family])
        return Utils.PRINTER_PROFILES_BY_FAMILY[family];
    return Utils.PRINTER_PROFILES;
};

Utils.ADDITIONNALCLASS_NONE     = "none";
Utils.ADDITIONNALCLASS_YELLOW   = "yellow";
Utils.ADDITIONNALCLASS_YELLOW2  = "yellow2";
Utils.ADDITIONNALCLASS_MAGENTA  = "magenta";
Utils.ADDITIONNALCLASS_MAGENTA2 = "magenta2";
Utils.ADDITIONNALCLASS_GREEN    = "green"
Utils.ADDITIONNALCLASS_GREEN2   = "green2"
Utils.ADDITIONNALCLASS_RED      = "red";
Utils.ADDITIONNALCLASS_RED2     = "red2";
Utils.ADDITIONNALCLASS_BLUE     = "blue";
Utils.ADDITIONNALCLASS_BLUE2    = "blue2";
Utils.ADDITIONNALCLASS_WHITE    = "white";
Utils.ADDITIONNALCLASS_BLACK    = "black";
Utils.ADDITIONNALCLASS_BROWN    = "brown";
Utils.ADDITIONNALCLASS_BROWN2   = "brown2";
Utils.ADDITIONNALCLASS_GREY     = "grey";
Utils.ADDITIONNALCLASS_GREY2    = "grey2";

Utils.ACCOUNTING_TYPE_SCREENING            = 'screening';
Utils.ACCOUNTING_TYPE_PASS                 = 'pass';
Utils.ACCOUNTING_TYPE_RENEWAL              = 'renewal';
Utils.ACCOUNTING_TYPE_ARTICLE              = 'article';
Utils.ACCOUNTING_TYPE_WALLET               = 'wallet';
Utils.ACCOUNTING_TYPE_SHIPPING             = 'shipping';
Utils.ACCOUNTING_TYPE_PAYMENT              = 'paymentfee';
Utils.ACCOUNTING_TYPE_SPLIT_PAYMENT        = 'payment';
Utils.ACCOUNTING_TYPE_GENERIC              = 'generic';
Utils.ACCOUNTING_TYPE_DISCOUNT             = 'discount';
Utils.ACCOUNTING_TYPE_TIP                  = 'tip';
Utils.ACCOUNTING_TYPE_CANCELLATION         = 'cancellation';
Utils.ACCOUNTING_TYPE_SCREENING_REFUND     = 'screening-refund';
Utils.ACCOUNTING_TYPE_PASS_REFUND          = 'pass-refund';
Utils.ACCOUNTING_TYPE_RENEWAL_REFUND       = 'renewal-refund';
Utils.ACCOUNTING_TYPE_ARTICLE_REFUND       = 'article-refund';
Utils.ACCOUNTING_TYPE_WALLET_REFUND        = 'wallet-refund';
Utils.ACCOUNTING_TYPE_SHIPPING_REFUND      = 'shipping-refund';
Utils.ACCOUNTING_TYPE_PAYMENT_REFUND       = 'paymentfee-refund';
Utils.ACCOUNTING_TYPE_SPLIT_PAYMENT_REFUND = 'payment-refund';
Utils.ACCOUNTING_TYPE_GENERIC_REFUND       = 'generic-refund';
Utils.ACCOUNTING_TYPE_DISCOUNT_REFUND      = 'discount-refund';
Utils.ACCOUNTING_TYPE_TIP_REFUND           = 'tip-refund';

Utils.INSTANCE_MODULES = {
    "base_modules": [
        "settings",
        "devices",
        "configuration_qr_codes",
        "equipment_catalog",
        "equipment_orders",
        "instance_equipment_config",
        "cron_tasks",
        "logs",
        "users",
        "ticketack_users",
        'tktInstances',
        "editions",
    ],
    "planning": [
        "programmations",
        "screenings",
        "tickets",
        "events",
        "sections",
        "distributors",
        "places",
        "tickettypes",
        "layouts",
        "pdf_layouts",
        "email_layouts",
        "pricinglists",
        "bucketlists",
        "salepoints",
        "cashregisters",
        "customers",
        "stats",
    ],
    "team": [
        "phases",
        "periods",
        "sectors",
        "plan",
        "form_layouts",
        "application_types",
        "applications",
    ],
    "workflows": [
        "workflows",
        "workflow_runs",
        "form_layouts",
    ],
    "shop": [
        "articles",
        "salepoints",
        "cashregisters",
        "customers",
        "articlecategories",
    ],
    "communications": [
        "messages",
    ],
    "publishing": [
        "posts",
        "post_categories",
        "channels",
    ],
    "resources": [
        "activities",
        "resources",
        "tasks",
    ]
};

Utils.MONGO_RESSOURCES = [
    "places",
    "tickets",
    "tickettypes",
    "layouts",
    "pdf_layouts",
    "email_layouts",
    "pricinglists",
    "bucketlists",
    "salepoints",
    "cashregisters",
    "customers",
    "articles",
    "articlecategories",
    "post_categories",
    "ticketack_users",
    "screenings",
    "cron_tasks",
    "editions",
    "workflows",
    "workflow_runs",
]

// Modules that unlock each top-bar section via User.anyRightInSection.
// Keep aligned with src/config/navigation.js: do not list rights that only
// belong to another section (e.g. customers → operation, stats → analysis),
// or Administration appears with an empty sidebar.
Utils.TOPBAR_MODULES = {
    "admin": [
        "programmations",
        "programmation_groups",
        "events",
        "sections",
        "distributors",
        "places",
        "tickettypes",
        "layouts",
        "pdf_layouts",
        "email_layouts",
        "form_layouts",
        "application_types",
        "workflows",
        "pricinglists",
        "bucketlists",
        "salepoints",
        "cashregisters",
        "posts",
        "post_categories",
        "channels",
        "articles",
        "users",
        "ticketack_users",
        "abilities_templates",
        "activities",
        "resources",
        "articlecategories",
        "phases",
        "periods",
        "sectors",
        "configuration_qr_codes",
        "cron_tasks",
        "devices",
        "equipment_catalog",
        "equipment_orders",
        "instance_equipment_config",
        "editions",
        "settings",
    ],
    "operation": [
        "customers",
        "messages",
        "tasks",
        "tickets",
        "screenings",
        "applications",
        "shifts",
        "logs",
        "workflows",
        "workflow_runs",
    ],
    "analysis": [
        "stats"
    ]
}

Utils.customerTypes = () => {
    return [
        Utils.CUSTOMER_TYPE_CINEMA,
        Utils.CUSTOMER_TYPE_CINEMA_FESTIVAL,
        Utils.CUSTOMER_TYPE_MUSIC,
        Utils.CUSTOMER_TYPE_MUSIC_FESTIVAL,
        Utils.CUSTOMER_TYPE_THEATER,
        Utils.CUSTOMER_TYPE_RECREATION_CENTER,
        Utils.CUSTOMER_TYPE_RECREATION_PARC,
        Utils.CUSTOMER_TYPE_SPORT,
        Utils.CUSTOMER_TYPE_MUSEUM,
        Utils.CUSTOMER_TYPE_GENERIC,
    ];
};

Utils.appSections = () => {
    return [
        { id: Utils.SECTION_ADMIN, icon: 'fa-cog' },
        { id: Utils.SECTION_OPERATION, icon: 'fa-rocket' },
        { id: Utils.SECTION_ANALYSIS, icon: 'fa-line-chart' },
    ];
};

Utils.modules = () => {
    return [
        Utils.MODULE_PLANNING,
        Utils.MODULE_SHOP,
        Utils.MODULE_RESOURCES,
        Utils.MODULE_COMMUNICATIONS,
        Utils.MODULE_PUBLISHING,
        Utils.MODULE_TEAM,
        Utils.MODULE_WORKFLOWS,
    ];
};

Utils.additionnalClasses = () => {
    return [
        Utils.ADDITIONNALCLASS_NONE,
        Utils.ADDITIONNALCLASS_YELLOW,
        Utils.ADDITIONNALCLASS_YELLOW2,
        Utils.ADDITIONNALCLASS_MAGENTA,
        Utils.ADDITIONNALCLASS_MAGENTA2,
        Utils.ADDITIONNALCLASS_GREEN,
        Utils.ADDITIONNALCLASS_GREEN2,
        Utils.ADDITIONNALCLASS_RED,
        Utils.ADDITIONNALCLASS_RED2,
        Utils.ADDITIONNALCLASS_BLUE,
        Utils.ADDITIONNALCLASS_BLUE2,
        Utils.ADDITIONNALCLASS_WHITE,
        Utils.ADDITIONNALCLASS_BLACK,
        Utils.ADDITIONNALCLASS_BROWN,
        Utils.ADDITIONNALCLASS_BROWN2,
        Utils.ADDITIONNALCLASS_GREY,
        Utils.ADDITIONNALCLASS_GREY2
    ]
}

Utils.event_types = (type = null) => {
    const types = [
        { "type": Utils.EVENT_TYPE_MOVIE, "label": "Film" },
        { "type": Utils.EVENT_TYPE_MUSIC_GROUP, "label": "Artiste/Groupe" },
        { "type": Utils.EVENT_TYPE_EXHIBITION, "label": "Exposition" },
        { "type": Utils.EVENT_TYPE_DRAMA, "label": "Pièce de théâtre" },
        { "type": Utils.EVENT_TYPE_SERVICE, "label": "Service" },
    ];

    return type ? types.find((r) => r.type === type) : types;
};

Utils.image_types = (usage, type = null) => {
    let types = null;
    switch (usage) {
        case 'event':
            types = [
                Utils.IMAGE_TYPE_POSTER,
                Utils.IMAGE_TYPE_BANNIERE,
                Utils.IMAGE_TYPE_STILL,
            ];
            break;
        case 'setting':
            types = [
                Utils.IMAGE_TYPE_LOGO,
                Utils.IMAGE_TYPE_LOGO_A4,
                Utils.IMAGE_TYPE_LOGO_THERMAL,
                Utils.IMAGE_TYPE_FAVICON,
                Utils.IMAGE_TYPE_APPICON,
                Utils.IMAGE_TYPE_PARTNERS,
                Utils.IMAGE_TYPE_PARTNERS_A4,
                Utils.IMAGE_TYPE_PARTNERS_THERMAL
            ];
            break;
        default:
            types = [
                Utils.IMAGE_TYPE_DEFAULT
            ];
            break;
    };

    return type ? types.find((r) => r === type) : types;
};

Utils.link_types = (type = null) => {
    const types = [
        Utils.LINK_TYPE_ARTICLE,
        Utils.LINK_TYPE_CATEGORY,
        Utils.LINK_TYPE_MOVIE,
        Utils.LINK_TYPE_SCREENING,
        Utils.LINK_TYPE_URL
    ];

    return type ? types.find((r) => r === type) : types;
};

Utils.service_types = (type = null) => {
    const types = [
        { "type": Utils.SERVICE_TYPE_GENERIC, "label": "-" },
        { "type": Utils.SERVICE_TYPE_KINDERCITY, "label": "KinderCity" },
        { "type": Utils.SERVICE_TYPE_BABY_PARK, "label": "Baby Park" },
        { "type": Utils.SERVICE_TYPE_KIDS_PARK, "label": "Kids Park" },
        { "type": Utils.SERVICE_TYPE_KIDS_ATELIER, "label": "Kids Atelier" },
        { "type": Utils.SERVICE_TYPE_KIDS_CAMP, "label": "Kids Camp" },
        { "type": Utils.SERVICE_TYPE_KIDS_GARDEN, "label": "Kids Garden" },
        { "type": Utils.SERVICE_TYPE_TEENS_STUDIO, "label": "Teens Studio" },
        { "type": Utils.SERVICE_TYPE_TEENS_ATELIER, "label": "Teens Atelier" },
        { "type": Utils.SERVICE_TYPE_TEENS_CAMP, "label": "Teens Camp" },
    ];

    return type ? types.find((r) => r.type === type) : types;
};

Utils.activities = () => {
    return [
        Utils.ACTIVITY_ACTOR,
        Utils.ACTIVITY_CAST,
        Utils.ACTIVITY_DIRECTOR,
        Utils.ACTIVITY_EDITOR,
        Utils.ACTIVITY_PHOTOGRAPHY,
        Utils.ACTIVITY_MUSIC,
        Utils.ACTIVITY_PRODUCER,
        Utils.ACTIVITY_SCREENPLAY
    ];
};

Utils.activitiesForEventType = (eventType) => {
    switch (eventType) {
        case Utils.EVENT_TYPE_DRAMA:
            return [
                Utils.ACTIVITY_ACTOR,
                Utils.ACTIVITY_STAGE_DIRECTION,
                Utils.ACTIVITY_ASSISTANT_STAGE_DIRECTION,
                Utils.ACTIVITY_DRAMATURGY,
                Utils.ACTIVITY_OUTSIDE_EYE,
                Utils.ACTIVITY_LIGHTING_DESIGN,
                Utils.ACTIVITY_SOUND_DESIGN,
                Utils.ACTIVITY_VIDEO_DESIGN,
                Utils.ACTIVITY_MUSIC,
                Utils.ACTIVITY_MUSICAL_CREATION,
                Utils.ACTIVITY_SCENOGRAPHY,
                Utils.ACTIVITY_SETS,
                Utils.ACTIVITY_MAKEUP,
                Utils.ACTIVITY_COSTUMES,
                Utils.ACTIVITY_SOUND_OPERATION,
                Utils.ACTIVITY_LIGHTING_OPERATION,
                Utils.ACTIVITY_ADMINISTRATION,
                Utils.ACTIVITY_PRODUCTION,
                Utils.ACTIVITY_TOURING,
                Utils.ACTIVITY_CHOREOGRAPHY,
                Utils.ACTIVITY_ASSISTANT_CHOREOGRAPHY,
                Utils.ACTIVITY_CONDUCTOR,
                Utils.ACTIVITY_COMPOSER,
                Utils.ACTIVITY_PROJECT_LEAD,
            ];
        case Utils.EVENT_TYPE_MUSIC_GROUP:
            return [
                Utils.ACTIVITY_MUSICIAN,
                Utils.ACTIVITY_VOCALIST,
                Utils.ACTIVITY_COMPOSER,
                Utils.ACTIVITY_ARRANGER,
                Utils.ACTIVITY_CONDUCTOR,
                Utils.ACTIVITY_ARTISTIC_DIRECTION,
                Utils.ACTIVITY_SOUND_ENGINEER,
                Utils.ACTIVITY_PRODUCER,
                Utils.ACTIVITY_GUEST,
                Utils.ACTIVITY_OTHER,
            ];
        case Utils.EVENT_TYPE_MOVIE:
        default:
            return Utils.activities();
    }
};

Utils.programmation_supports = () => {
    return [
        Utils.PROGRAMMATION_SUPPORT_DCP,
        Utils.PROGRAMMATION_SUPPORT_DVD,
        Utils.PROGRAMMATION_SUPPORT_BLU_RAY,
        Utils.PROGRAMMATION_SUPPORT_VIDEO_FILE,
        Utils.PROGRAMMATION_SUPPORT_OTHER
    ];
};

Utils.days = (index) => {
    const days = [
        { "index": 1, "label": Utils.MONDAY, "full_label": Utils.MONDAY_FULL },
        { "index": 2, "label": Utils.TUESDAY, "full_label": Utils.TUESDAY_FULL },
        { "index": 3, "label": Utils.WEDNESDAY, "full_label": Utils.WEDNESDAY_FULL },
        { "index": 4, "label": Utils.THURSDAY, "full_label": Utils.THURSDAY_FULL },
        { "index": 5, "label": Utils.FRIDAY, "full_label": Utils.FRIDAY_FULL },
        { "index": 6, "label": Utils.SATURDAY, "full_label": Utils.SATURDAY_FULL },
        { "index": 0, "label": Utils.SUNDAY, "full_label": Utils.SUNDAY_FULL }
    ];

    return index >= 0 ? days.find((d) => d.index === index) : days;
};

Utils.tktUserRoles = (system) => {
    if (!system)
        return [
            Utils.TKTUSER_ROLE_ADMIN,
            Utils.TKTUSER_ROLE_POS,
            Utils.TKTUSER_ROLE_ON_SITE,
            Utils.TKTUSER_ROLE_PARTNER,
            Utils.TKTUSER_ROLE_OVERBOOKER,
            Utils.TKTUSER_ROLE_TRANSFER,
            Utils.TKTUSER_ROLE_ACCRED,
            Utils.TKTUSER_ROLE_TICKET_EDITOR,
            Utils.TKTUSER_ROLE_REFUND,
            Utils.TKTUSER_ROLE_WALLET_CASHIER,
            Utils.TKTUSER_ROLE_WALLET_MANAGER,
            Utils.TKTUSER_ROLE_ACCOUNTING,
            Utils.TKTUSER_ROLE_STATS,
            Utils.TKTUSER_ROLE_CUSTOMER,
        ];

    return [
        Utils.TKTUSER_ROLE_ESHOP,
        Utils.TKTUSER_ROLE_PIHYVE,
        Utils.TKTUSER_ROLE_ENTRY_CHECK,
        Utils.TKTUSER_ROLE_MOBILE,
        Utils.TKTUSER_ROLE_ADMIN,
        Utils.TKTUSER_ROLE_POS,
        Utils.TKTUSER_ROLE_ON_SITE,
        Utils.TKTUSER_ROLE_PARTNER,
        Utils.TKTUSER_ROLE_OVERBOOKER,
        Utils.TKTUSER_ROLE_TRANSFER,
        Utils.TKTUSER_ROLE_ACCRED,
        Utils.TKTUSER_ROLE_TICKET_EDITOR,
        Utils.TKTUSER_ROLE_REFUND,
        Utils.TKTUSER_ROLE_WALLET_CASHIER,
        Utils.TKTUSER_ROLE_WALLET_MANAGER,
        Utils.TKTUSER_ROLE_ACCOUNTING,
        Utils.TKTUSER_ROLE_STATS,
        Utils.TKTUSER_ROLE_CUSTOMER,
        Utils.TKTUSER_ROLE_INTEGRATOR,
    ];
}

Utils.pricingRoles = () => {
    return [
        Utils.PRICING_ROLE_ESHOP,
        Utils.PRICING_ROLE_ON_SITE,
        Utils.PRICING_ROLE_PARTNER,
        Utils.PRICING_ROLE_ACCRED,
        Utils.PRICING_ROLE_ADMIN,
        Utils.PRICING_ROLE_MOBILE,
        Utils.PRICING_ROLE_CUSTOMER
    ];
};

/**
 * Default seller roles for newly created pricings / article discounts.
 * Restricted to on-site + admin so a tariff is never public (eshop) by accident.
 *
 * @returns {string[]}
 */
Utils.defaultPricingSellerRoles = () => {
    return [
        Utils.PRICING_ROLE_ON_SITE,
        Utils.PRICING_ROLE_ADMIN,
    ];
};

Utils.bucketRoles = () => {
    // all non system roles plus eshop, entry-check and mobile
    return [
        Utils.TKTUSER_ROLE_ESHOP,
        Utils.TKTUSER_ROLE_ENTRY_CHECK,
        Utils.TKTUSER_ROLE_MOBILE,
        Utils.TKTUSER_ROLE_ADMIN,
        Utils.TKTUSER_ROLE_POS,
        Utils.TKTUSER_ROLE_ON_SITE,
        Utils.TKTUSER_ROLE_PARTNER,
        Utils.TKTUSER_ROLE_OVERBOOKER,
        Utils.TKTUSER_ROLE_TRANSFER,
        Utils.TKTUSER_ROLE_ACCRED,
        Utils.TKTUSER_ROLE_TICKET_EDITOR,
        Utils.TKTUSER_ROLE_REFUND,
        Utils.TKTUSER_ROLE_WALLET_CASHIER,
        Utils.TKTUSER_ROLE_WALLET_MANAGER,
        Utils.TKTUSER_ROLE_ACCOUNTING,
        Utils.TKTUSER_ROLE_STATS,
        Utils.TKTUSER_ROLE_CUSTOMER,
    ];
};

Utils.periods = ({ allowCinemaWeek = true } = {}) => {
    const all = [
        Utils.PERIOD_DAY,
        Utils.PERIOD_WEEK,
        Utils.PERIOD_CINEMA_WEEK,
        Utils.PERIOD_MONTH,
        Utils.PERIOD_YEAR,
    ];
    if (allowCinemaWeek)
        return all;
    return all.filter((p) => p !== Utils.PERIOD_CINEMA_WEEK);
};

/**
 * Cinema week starts on Wednesday. Advance to the next Wednesday
 * relative to `date` (keep the day when it is already a Wednesday).
 *
 * @param {moment.Moment|Date|string|null|undefined} date
 * @returns {moment.Moment}
 */
Utils.snap_to_next_wednesday = function (date) {
    const m = moment(date);
    const start = (m.isValid() ? m : moment()).clone().startOf('day');
    const iso = start.isoWeekday(); // Mon=1 … Sun=7; Wed=3
    if (iso === 3)
        return start;
    return start.add((3 - iso + 7) % 7, 'days');
};

/**
 * Snap start_at when the period type requires it (cinema week → Wednesday).
 *
 * @param {moment.Moment|Date|string|null|undefined} start_at
 * @param {string|null|undefined} period_type
 * @returns {moment.Moment}
 */
Utils.snap_start_at_for_period_type = function (start_at, period_type) {
    if (period_type === Utils.PERIOD_CINEMA_WEEK)
        return Utils.snap_to_next_wednesday(start_at);
    const m = moment(start_at);
    return (m.isValid() ? m : moment()).clone().startOf('day');
};

Utils.durations = () => {
    return [
        Utils.DURATION_5_MIN,
        Utils.DURATION_10_MIN,
        Utils.DURATION_15_MIN,
        Utils.DURATION_30_MIN,
        Utils.DURATION_45_MIN,
        Utils.DURATION_1_HOUR,
        Utils.DURATION_6_HOURS,
        Utils.DURATION_12_HOURS,
        Utils.DURATION_1_DAY,
        Utils.DURATION_7_DAYS,
        Utils.DURATION_1_MONTH,
        Utils.DURATION_6_MONTHS,
        Utils.DURATION_1_YEAR,
        Utils.DURATION_18_MONTHS,
        Utils.DURATION_2_YEARS,
        Utils.DURATION_5_YEARS,
    ];
};

Utils.applicationStatus = () => {
    return [
        Utils.APPLICATION_STATUS_SUBMITTED,
        Utils.APPLICATION_STATUS_ACCEPTED,
        Utils.APPLICATION_STATUS_REJECTED,
    ];
};

Utils.ticketStatus = () => {
    return [
        Utils.TICKET_STATUS_NEW,
        Utils.TICKET_STATUS_PENDING,
        Utils.TICKET_STATUS_ACTIVATED,
        Utils.TICKET_STATUS_BLOCKED
    ];
};

Utils.taskStatus = () => {
    return [
        Utils.TASK_STATUS_NEW,
        Utils.TASK_STATUS_FREE,
        Utils.TASK_STATUS_ASSIGNED,
        Utils.TASK_STATUS_CLAIMED,
        Utils.TASK_STATUS_RENOUNCED
    ];
};

Utils.notifications = () => {
    return [
        Utils.ACTIVITY_NOTIFICATION_NONE,
        Utils.ACTIVITY_NOTIFICATION_PT10M,
        Utils.ACTIVITY_NOTIFICATION_PT30M,
        Utils.ACTIVITY_NOTIFICATION_PT1H,
        Utils.ACTIVITY_NOTIFICATION_PT2H,
        Utils.ACTIVITY_NOTIFICATION_PT6H,
        Utils.ACTIVITY_NOTIFICATION_P1D,
        Utils.ACTIVITY_NOTIFICATION_P2D,
        Utils.ACTIVITY_NOTIFICATION_P3D
    ];
};

Utils.posLayouts = () => {
    return [
        Utils.POS_LAYOUT_TICKETS,
        Utils.POS_LAYOUT_ARTICLES,
    ];
};

Utils.posPaymentMethods = () => {
    return [
        Utils.POS_PAYMENT_METHOD_CASH,
        Utils.POS_PAYMENT_METHOD_CASH_COLLECTOR,
        Utils.POS_PAYMENT_METHOD_EWALLET,
        Utils.POS_PAYMENT_METHOD_SUMUP,
        Utils.POS_PAYMENT_METHOD_ZVT_EFT,
        Utils.POS_PAYMENT_METHOD_OTHER_EFT,
        Utils.POS_PAYMENT_METHOD_TWINT,
        Utils.POS_PAYMENT_METHOD_PIMPAY,
        Utils.POS_PAYMENT_METHOD_POS_TRANSFER,
    ];
};

Utils.eshopPaymentMethods = () => {
    return [
        Utils.ESHOP_PAYMENT_METHOD_FREE,
        Utils.ESHOP_PAYMENT_METHOD_PROXYPAY,
        Utils.ESHOP_PAYMENT_METHOD_LATER,
        Utils.ESHOP_PAYMENT_METHOD_ESHOP_TRANSFER,
    ];
};

Utils.paymentMethods = () => {
    return [
        Utils.PAYMENT_METHOD_CASH,
        Utils.PAYMENT_METHOD_CASH_COLLECTOR,
        Utils.PAYMENT_METHOD_EWALLET,
        Utils.PAYMENT_METHOD_SUMUP,
        Utils.PAYMENT_METHOD_ZVT_EFT,
        Utils.PAYMENT_METHOD_OTHER_EFT,
        Utils.PAYMENT_METHOD_TWINT,
        Utils.PAYMENT_METHOD_PIMPAY,
        Utils.PAYMENT_METHOD_PROXYPAY,
        Utils.PAYMENT_METHOD_PROXYPAY_ALT,
        Utils.PAYMENT_METHOD_TRANSFER,
        Utils.PAYMENT_METHOD_LATER,
        Utils.PAYMENT_METHOD_FREE,
    ];
}

Utils.showCalculatorOptions = () => {
    return [
        Utils.SHOW_CALCULATOR_OPTIONAL,
        Utils.SHOW_CALCULATOR_ALWAYS,
        Utils.SHOW_CALCULATOR_NEVER
    ];
};

Utils.cashOutflowComputeModeOptions = () => {
    return [
        Utils.CASH_OUTFLOW_COMPUTE_MODE_AUTO,
        Utils.CASH_OUTFLOW_COMPUTE_MODE_ZERO,
        Utils.CASH_OUTFLOW_COMPUTE_MODE_NONE,
    ];
};

Utils.passOwnerFields = () => {
    return [
        Utils.USER_FIELD_COMPANY,
        Utils.USER_FIELD_TITLE,
        Utils.USER_FIELD_FIRSTNAME,
        Utils.USER_FIELD_LASTNAME,
        Utils.USER_FIELD_EMAIL,
        Utils.USER_FIELD_ADDRESS,
        Utils.USER_FIELD_ZIP,
        Utils.USER_FIELD_CITY,
        Utils.USER_FIELD_COUNTRY,
        Utils.USER_FIELD_AGE,
        Utils.USER_FIELD_BIRTHDATE,
        Utils.USER_FIELD_PHOTO,
        Utils.USER_FIELD_SEX,
        Utils.USER_FIELD_PHONE,
        Utils.USER_FIELD_CELLPHONE,
        Utils.USER_FIELD_GIFT_MESSAGE,
        Utils.USER_FIELD_COMMENT,
    ];
};

Utils.userDataFields = () => {
    return [
        Utils.USER_FIELD_COMPANY,
        Utils.USER_FIELD_TITLE,
        Utils.USER_FIELD_FIRSTNAME,
        Utils.USER_FIELD_LASTNAME,
        Utils.USER_FIELD_EMAIL,
        Utils.USER_FIELD_ADDRESS,
        Utils.USER_FIELD_ZIP,
        Utils.USER_FIELD_CITY,
        Utils.USER_FIELD_COUNTRY,
        Utils.USER_FIELD_AGE,
        Utils.USER_FIELD_BIRTHDATE,
        Utils.USER_FIELD_SEX,
        Utils.USER_FIELD_PHONE,
        Utils.USER_FIELD_CELLPHONE,
        Utils.USER_FIELD_TABLE_NUMBER,
        Utils.USER_FIELD_COMMENT,
    ];
};

Utils.creditWalletModes = () => {
    return [
        Utils.CREDIT_WALLET_MODE_CREDIT,
        Utils.CREDIT_WALLET_MODE_DEBIT,
        Utils.CREDIT_WALLET_MODE_REACH,
        Utils.CREDIT_WALLET_MODE_REACH_FROM_BALANCE
    ];
};

Utils.creditArticleModes = () => Utils.creditWalletModes();

Utils.sexFields = () => {
    return [
        Utils.SEX_NC = 'n/c',
        Utils.SEX_F  = 'f',
        Utils.SEX_M  = 'm',
        Utils.SEX_O  = 'o'
    ];
};

Utils.titleFields = () => {
    return [
        Utils.TITLE_NC   = 'n/c',
        Utils.TITLE_MRS  = 'mrs',
        Utils.TITLE_MISS = 'miss',
        Utils.TITLE_MR   = 'mr',
        Utils.TITLE_DR   = 'dr',
    ];
};

Utils.envs = () => {
    return [
        Utils.ENV_TEST,
        Utils.ENV_PROD,
    ];
};

Utils.registrationCallbackModes = () => {
    return [
        Utils.REGISTRATION_CALLBACK_MODE_EMAIL,
        Utils.REGISTRATION_CALLBACK_MODE_CALLBACK,
        Utils.REGISTRATION_CALLBACK_MODE_URL,
        Utils.REGISTRATION_CALLBACK_MODE_MAILCHIMP,
        Utils.REGISTRATION_CALLBACK_MODE_BREVO,
        Utils.REGISTRATION_CALLBACK_MODE_GETRESPONSE,
    ];
};

Utils.donationModes = () => {
    return [
        Utils.DONATION_MODE_CHECKOUT,
        Utils.DONATION_MODE_HEADER,
        Utils.DONATION_MODE_FOOTER,
        Utils.DONATION_MODE_EMAIL,
    ];
};

Utils.twintMethods = () => {
    return [
        Utils.TWINT_METHOD_STICKER,
        Utils.TWINT_METHOD_BEACON,
    ];
};

Utils.deliveryTypes = () => {
    return [
        Utils.DELIVERY_TYPE_ONSITE,
        Utils.DELIVERY_TYPE_TAKEAWAY,
        Utils.DELIVERY_TYPE_PICKUP,
        Utils.DELIVERY_TYPE_SHIPMENT,
        Utils.DELIVERY_TYPE_VIRTUAL
    ];
};

Utils.bookingModes = () => {
    return [
        Utils.BOOKING_MODE_FREE,
        Utils.BOOKING_MODE_EMAIL,
        Utils.BOOKING_MODE_OTHER,
    ];
};

Utils.accountingTypes = () => {
    return [
        Utils.ACCOUNTING_TYPE_SCREENING,
        Utils.ACCOUNTING_TYPE_PASS,
        Utils.ACCOUNTING_TYPE_RENEWAL,
        Utils.ACCOUNTING_TYPE_ARTICLE,
        Utils.ACCOUNTING_TYPE_WALLET,
        Utils.ACCOUNTING_TYPE_SHIPPING,
        Utils.ACCOUNTING_TYPE_PAYMENT,
        Utils.ACCOUNTING_TYPE_SPLIT_PAYMENT,
        Utils.ACCOUNTING_TYPE_GENERIC,
        Utils.ACCOUNTING_TYPE_DISCOUNT,
        Utils.ACCOUNTING_TYPE_TIP,
        Utils.ACCOUNTING_TYPE_CANCELLATION,
        Utils.ACCOUNTING_TYPE_SCREENING_REFUND,
        Utils.ACCOUNTING_TYPE_PASS_REFUND,
        Utils.ACCOUNTING_TYPE_RENEWAL_REFUND,
        Utils.ACCOUNTING_TYPE_ARTICLE_REFUND,
        Utils.ACCOUNTING_TYPE_WALLET_REFUND,
        Utils.ACCOUNTING_TYPE_SHIPPING_REFUND,
        Utils.ACCOUNTING_TYPE_PAYMENT_REFUND,
        Utils.ACCOUNTING_TYPE_SPLIT_PAYMENT_REFUND,
        Utils.ACCOUNTING_TYPE_GENERIC_REFUND,
        Utils.ACCOUNTING_TYPE_DISCOUNT_REFUND,
        Utils.ACCOUNTING_TYPE_TIP_REFUND
    ];
};

Utils.dotify = (obj) => {
    var res = {};
    function recurse(obj, current) {
        for (var key in obj) {
            var value = obj[key];
            var newKey = (current ? current + '.' + key : key);  // joined key with dot
            if (value && typeof value === 'object') {
                recurse(value, newKey);  // it's a nested object, so do it again
            } else {
                res[newKey] = value;  // it's not an object, so set the property
            }
        }
    }

    recurse(obj);
    return res;
};

Utils.getDotified = (obj, dotifiedNotation) => {
    if (!obj)
        return null;

    return dotifiedNotation.split('.').reduce((a, b) => a ? a[b] : null, obj);
}

Utils.assignDotified = (obj, prop, value) => {
    if (typeof prop === "string")
        prop = prop.split(".");

    if (prop.length > 1) {
        var e = prop.shift();
        Utils.assignDotified(
            obj[e] = Object.prototype.toString.call(obj[e]) === "[object Object]" ? obj[e] : {},
            prop,
            value
        );
    } else {
        obj[prop[0]] = value;
    }
}

Utils.removeDotified = (obj, keyString, cleanEmptyProperties) => {
    const keys = typeof keyString === "string" ? keyString.split('.') : keyString;

    if (keys.length === 1) {
        delete obj[keys[0]];
        return obj;
    }

    obj[keys[0]] = Utils.removeDotified(obj[keys[0]], keys.slice(1), cleanEmptyProperties);

    if (cleanEmptyProperties && !Object.keys(obj[keys[0]]).length)
        delete obj[keys[0]];

    return obj;
}

Utils.normalizeBoolean = (value) => {
    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return value;
};

/**
 * Trim and strip all trailing slashes from an eShop base URI.
 * Matches engine mailer/eshop_uri.normalizeUri.
 */
Utils.normalizeEshopUri = (value) => {
    if (typeof value !== 'string')
        return '';
    return value.trim().replace(/\/+$/, '');
};

/**
 * redux-form Field normalize: strip trailing slashes once a host is present,
 * without turning `https://` into `https:` while the user is still typing.
 */
Utils.normalizeEshopUriInput = (value) => {
    if (typeof value !== 'string')
        return value;
    if (!/^https?:\/\/[^/]/i.test(value.trim()))
        return value;
    return Utils.normalizeEshopUri(value);
};

Utils.normalizeInt = (value) => {
    return parseInt(value, 10);
};

Utils.ucfirst = (string) => {
    if (!_.isString(string) || string.length === 0)
        return "";

    if (string.length === 1)
        return string.charAt(0).toUpperCase();

    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

Utils.langs = () => {
    if (store === undefined)
        return Utils.DEFAULT_LANGS;

    let langs;
    try {
        langs = store.getState().settings.setting?.l10n?.locales?.all || Utils.DEFAULT_LANGS;
    } catch (e) {
        // Redux disallows getState() while a reducer runs; plain-object hydration may still
        // construct models (e.g. FormLayout) that call Utils.langs().
        langs = Utils.DEFAULT_LANGS;
    }
    return langs.map((lang) => {
        return lang.split("_")[0]
    })
}

Utils.locales = () => {
    return Utils.LOCALES;
}

Utils.default_lang = () => {
    if (Utils.langs().length) {
        return Utils.langs()[0];
    }

    return 'fr';
};

/** Navigate to /messages with location state so MessageCreate can return to the caller. */
Utils.push_messages_with_back = (navigate, location, extraState = {}) => {
    navigate_to_messages_compose(navigate, location, extraState);
};

/** ISO 639-1 code for UI (languages.*, active menu item); maps fr-CH -> fr. */
Utils.i18nUiLang = (i18n) => {
    const raw = i18n?.resolvedLanguage || i18n?.language || 'fr';
    return String(raw).split(/[-_]/)[0].toLowerCase();
};

Utils.currency = () => store.getState().settings.setting?.l10n?.currency || 'CHF';
Utils.tax      = () => store.getState().settings.setting?.customer?.tax;
Utils.imgProxyHost = () => (store.getState().settings.setting?.integrations?.weserv?.proxy_img_host || '').trim();

/** Lossy weserv quality for non-WebP sources. Keep in sync across Ticketack proxies. */
Utils.IMG_PROXY_LOSSY_QUALITY = 90;
/**
 * Weserv always re-encodes; omitting &q= uses default quality 80
 * (https://wsrv.nl/docs/format#quality). They refuse to return the original
 * bytes even when dimensions are unchanged
 * (https://github.com/weserv/images/issues/82 — wontfix).
 * For Kronos / already-WebP sources we still use the CDN but pass q matching
 * Kronos IMAGE_WEBP_QUALITY (0.92).
 */
Utils.IMG_PROXY_TRUSTED_QUALITY = 92;
Utils.IMG_PROXY_LOSSLESS_MAX_PX = 64;

Utils.imgProxyIsWebpUrl = (url) => /\.webp([?#]|$)/i.test(String(url || ''));

/** Kronos-hosted posters/assets — already optimized; proxy for CDN only. */
Utils.imgProxyIsTrustedKronosUrl = (url) => {
    try {
        return /kronos/i.test(new URL(url, window.location.origin).hostname);
    } catch (e) {
        return false;
    }
};

/**
 * Weserv image proxy URL.
 *
 * Keep in sync with:
 * - eshop AppRouter::img_proxy_url
 * - ticketack-wordpress-module tkt_img_proxy_url
 * - js-lib / rn-ticketack-mobile AssetsHelper.proxyUrl
 * - ticketack-app RemoteImage.proxyUrl
 * - engine mailer ImageWidget / LibraryImageWidget (email exception: jpg/png, q=90, no webp/ll)
 *
 * Shared contract (CDN cache hits + consistent behaviour):
 * - stable query order: url, w, h, fit, we, [output], [q|ll]
 * - always &we (never upscale a smaller source)
 * - fit=outside
 * - trusted Kronos host OR source already .webp → no output, q=92
 *   (weserv always re-encodes; default q=80 if omitted —
 *   https://wsrv.nl/docs/format#quality ; no original-bytes passthrough —
 *   https://github.com/weserv/images/issues/82)
 * - otherwise output=webp; longest side <= 64 → &ll; else q=90
 * - omit null / '' / '-' dimensions
 * - retina via extra w/h (srcset w descriptors / image-set 2x), not &dpr=
 */
Utils.imgProxyUrl = (url, max_width = null, max_height = null) => {
    if (!url)
        return url;

    const proxyHost = Utils.imgProxyHost();
    if (!proxyHost)
        return url;

    if (typeof url !== 'string')
        return url;

    if (url.startsWith('data:') || url.startsWith('blob:'))
        return url;

    let sourceUrl = url;
    try {
        sourceUrl = new URL(url, window.location.origin).toString();
    } catch (e) {
        sourceUrl = url;
    }

    try {
        if (new URL(sourceUrl).host === proxyHost)
            return sourceUrl;
    } catch (e) {
        // Ignore invalid URL parsing and continue with best effort proxying
    }

    const w = (max_width && max_width !== '-') ? max_width : null;
    const h = (max_height && max_height !== '-') ? max_height : null;
    const parts = [`url=${encodeURIComponent(sourceUrl)}`];
    if (w)
        parts.push(`w=${encodeURIComponent(w)}`);
    if (h)
        parts.push(`h=${encodeURIComponent(h)}`);
    parts.push('fit=outside', 'we');

    const skipFormatConvert = Utils.imgProxyIsTrustedKronosUrl(sourceUrl)
        || Utils.imgProxyIsWebpUrl(sourceUrl);
    if (skipFormatConvert) {
        // Must set q: weserv defaults to 80 and never returns original bytes
        // (https://wsrv.nl/docs/format#quality ,
        //  https://github.com/weserv/images/issues/82).
        parts.push(`q=${Utils.IMG_PROXY_TRUSTED_QUALITY}`);
    } else {
        parts.push('output=webp');
        const longest = Math.max(parseInt(w, 10) || 0, parseInt(h, 10) || 0);
        if (longest > 0 && longest <= Utils.IMG_PROXY_LOSSLESS_MAX_PX)
            parts.push('ll');
        else
            parts.push(`q=${Utils.IMG_PROXY_LOSSY_QUALITY}`);
    }

    return `//${proxyHost}?${parts.join('&')}`;
}

Utils.imgProxySrcsetWidths = (max_width) => {
    const width = parseInt(max_width, 10) || 0;
    if (width <= 0)
        return [];
    const widths = [];
    const half = Math.round(width / 2);
    if (half >= 200 && half < width)
        widths.push(half);
    widths.push(width, width * 2);
    return [...new Set(widths)];
};

Utils.imgProxyScaleHeight = (max_height, src_width, dst_width) => {
    if (max_height == null || max_height === '' || max_height === '-')
        return max_height === '-' ? '-' : max_height;
    const src = parseInt(src_width, 10) || 0;
    const dst = parseInt(dst_width, 10) || 0;
    const height = parseInt(max_height, 10) || 0;
    if (src <= 0 || dst <= 0 || height <= 0)
        return max_height;
    return Math.round(height * dst / src);
};

Utils.imgProxySrcset = (url, max_width = null, max_height = null) => {
    const src_width = parseInt(max_width, 10) || 0;
    return Utils.imgProxySrcsetWidths(max_width).map((width) => (
        `${Utils.imgProxyUrl(url, width, Utils.imgProxyScaleHeight(max_height, src_width, width))} ${width}w`
    )).join(', ');
};

Utils.imgProxySizes = (max_width) => {
    const width = parseInt(max_width, 10) || 0;
    if (width <= 0)
        return '100vw';
    return `(max-width: ${width}px) 100vw, ${width}px`;
};

Utils.imgProxyImageSet = (url, max_width = null, max_height = null) => {
    const url_1x = Utils.imgProxyUrl(url, max_width, max_height);
    const width = parseInt(max_width, 10) || 0;
    if (width <= 0)
        return `url('${url_1x}')`;
    const url_2x = Utils.imgProxyUrl(
        url,
        width * 2,
        Utils.imgProxyScaleHeight(max_height, width, width * 2)
    );
    return `image-set(url('${url_1x}') 1x, url('${url_2x}') 2x)`;
};

Utils.localeFromLang = lang => {
    const map = {};
    Utils.DEFAULT_LANGS.forEach((lang, i) => {
        map[lang] = Utils.LOCALES[i];
    });

    return map[lang] ?? Utils.LOCALES[0];
};

/** i18n tag (e.g. fr / fr-CH) → moment locale for Kronos UI dates.
 * Swiss separators: fr → fr-ch (DD.MM.YYYY), de → de-ch. */
Utils.momentLocale = (lang) => {
    if (!lang || typeof lang !== 'string')
        return 'en';
    const base = lang.split(/[-_]/)[0].toLowerCase();
    if (base === 'fr')
        return 'fr-ch';
    if (base === 'de')
        return 'de-ch';
    if (base === 'en' || base === 'it')
        return base;
    return 'en';
};

/**
 * Moment locale for PDF canvas preview — matches Engine PdfLayout:
 * fr → fr-ch (dots), en → en-gb, de → de-ch.
 */
Utils.pdfMomentLocale = (lang) => {
    if (!lang || typeof lang !== 'string')
        return 'en-gb';
    const base = lang.split('-')[0].toLowerCase();
    if (base === 'fr')
        return 'fr-ch';
    if (base === 'de')
        return 'de-ch';
    if (base === 'en')
        return 'en-gb';
    if (base === 'it')
        return 'it';
    return 'en-gb';
};

/**
 * Localized date/time using moment (e.g. format "L LT" for short date + time).
 * @param {string|Date|number|object} iso  (object = moment instance)
 * @param {string} lang
 * @param {string} [format]
 */
Utils.formatLocalizedDateTime = (iso, lang, format = 'L LT') => {
    if (iso == null || iso === '')
        return '—';
    // Moment instances may carry a stale per-instance locale; always re-parse from
    // an instant so `.locale(lang)` applies reliably (also avoids dual-bundle quirks).
    const raw = moment.isMoment(iso) ? iso.toISOString() : iso;
    const m = moment(raw);
    if (!m.isValid())
        return '—';
    return m.locale(Utils.momentLocale(lang)).format(format);
};

Utils.formatNumber = (number, lang) => {
    lang = lang || Utils.default_lang();
    const locale = Utils.localeFromLang(lang).replace('_', '-');

    return new Intl.NumberFormat(locale, { style: "decimal" }).format(number);
}

Utils.formatMoney = (amount, lang, currency) => {
    lang     = lang || Utils.default_lang();
    currency = currency || Utils.currency();

    const locale = Utils.localeFromLang(lang).replace('_', '-');

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
    }).format(amount);
}

Utils.formatPhoneNumber = (number) => {
    if (number == null || number === '')
        return number;
    const presented = phonePresentation(number);
    return presented ? presented.display : number;
}

Utils.getPhoneNumberUri = (number) => {
    if (number == null || number === '')
        return `tel:${number ?? ''}`;
    const presented = phonePresentation(number);
    return presented ? presented.uri : `tel:${number}`;
}

Utils.resolveEventTranslatedTitle = resolveEventTranslatedTitle;
Utils.resolveEventTitleDisplay = resolveEventTitleDisplay;
Utils.eventTitleExportPair = eventTitleExportPair;

Utils.localized_or_fallback = (obj, lang, def) => {
    def = def || '';

    if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        return def;

    if (obj[lang])
        return obj[lang];

    const default_lang = Utils.default_lang();
    if (default_lang && default_lang !== lang && obj[default_lang])
        return obj[default_lang];

    const fallback = [...Utils.langs(), ...Object.keys(obj)].map(l => {
        if (l !== lang && l !== default_lang && obj[l]) {
            return `${obj[l]}`;
        }
        return undefined;
    }).filter(Boolean);

    return fallback.length ? fallback[0] : def;
}

Utils.new_pricing_names = (label, lang) => {
    const names = {};
    if (label && lang) {
        names[lang] = label;
    }
    return names;
}

/**
 * Empty translatable map for form defaults.
 * Prefer {} over pre-seeding every instance locale with "" — TranslatableField
 * builds its tabs from Utils.langs(), and prepareForUpdate cleans empties.
 */
Utils.default_translatable_fields = () => ({});

/**
 * Sort a parent/child collection into depth-first order and set `depth` on each
 * item (0 = root). Names are left unchanged — use {@link Utils.tree_depth_class}
 * on Semantic UI dropdown options (or list cells) for visual indentation.
 */
Utils.sortAndIndentCats = (cats, idField = '_id', parentField = 'parent') => {
    const catsCopy = _.cloneDeep(cats);
    let map = {};
    catsCopy.forEach((c) => {
        c.children = [];
        map[c[idField]] = c;
    });
    catsCopy.forEach((c) => {
        if (c[parentField]) {
            if (map[c[parentField]])
                map[c[parentField]].children.push(c);
            else
                c[parentField] = null;
        }
    });
    Object.keys(map).forEach((id) => { map[id][parentField] && delete map[id]; });

    let enhanced = [];
    const indentAndSort = (root, level) => {
        const defaultLang = Utils.default_lang();
        const cats = _.sortBy(root, (c) => {
            const name = c.name;
            if (name && typeof name === 'object')
                return name[defaultLang] || '';
            return name || '';
        });
        cats.forEach((c) => {
            c.depth = level;
            enhanced.push(c);
            indentAndSort(c.children, level + 1);
        });
    };
    indentAndSort(_.values(map), 0);

    return _.map(enhanced, (c) => {
        delete c.children;
        return c;
    });
};

/** Semantic UI / list class for hierarchical indentation (see treeSelect.scss). */
Utils.tree_depth_class = (depth) => {
    const d = Math.max(0, Math.min(Number(depth) || 0, 8));
    return `tree-depth-${d}`;
};

Utils._sort_languages = (values, current_lang) => {
    const topLanguages = ['fr', 'en', 'de', 'it'];

    const languages = Object.keys(values)
        .sort(
            (a, b) =>
                Utils.localized_or_fallback(iso639.get_languages_for_code(a), current_lang) > Utils.localized_or_fallback(iso639.get_languages_for_code(b), current_lang) ? 1 : -1
        )
        .map(code => { return { code, language: Utils.localized_or_fallback(iso639.get_languages_for_code(code), current_lang) } })

    const top = _.remove(languages, (l) => topLanguages.includes(l.code));

    return [...top, ...languages];
};

/**
 * Pseudo-language used for silent movies / no dialog.
 *
 * We intentionally keep it out of iso639.get_all() in UI lists to avoid
 * exposing other "extra" codes (e.g. no_sub, z* combinations) where not wanted.
 *
 * Origin: Net Oxygen iso639 fork already defines `no_lang` in `extra`.
 * We surface it explicitly as a first-class choice in "audio" fields.
 *
 * Maccsbox mapping: `no_lang` must be exported as "XX" (handled on export side).
 */
Utils.no_dialog_language_entry = (current_lang) => {
    const raw = iso639.get_languages_for_code('no_lang');
    const label = Utils.localized_or_fallback(raw, current_lang) || 'No dialog';
    return { code: 'no_lang', language: label };
};

Utils.is_no_dialog_language_code = (code) => {
    const c = String(code || '').toLowerCase();
    return c === 'no_lang';
};

/**
 * ISO 639 standard languages, optionally prefixed with the pseudo-code `no_lang` (silent / no dialog).
 *
 * @param {string|null|undefined} current_lang UI language code (e.g. fr, en) for labels
 * @param {{ includeNoDialog?: boolean }} [options]
 * @returns {{ code: string, language: string }[]}
 */
Utils.sorted_standard_languages = (current_lang, options = {}) => {
    const includeNoDialog = !!options.includeNoDialog;
    const lang = current_lang
        ? String(current_lang).split(/[-_]/)[0].toLowerCase()
        : Utils.default_lang();
    const sorted = Utils._sort_languages(iso639.get_standard(), lang);
    if (includeNoDialog) {
        return [Utils.no_dialog_language_entry(lang), ...sorted];
    }
    return sorted;
};

/**
 * Human-readable label for a standard language code (programmation cards, etc.).
 *
 * @param {string|null|undefined} code ISO language code
 * @param {string} current_lang UI language
 * @returns {string} empty string if no code
 */
Utils.standard_language_display_name = function (code, current_lang) {
    if (code === '' || code == null) {
        return '';
    }
    if (Utils.is_no_dialog_language_code(code)) {
        return Utils.no_dialog_language_entry(current_lang).language;
    }
    const list = Utils.sorted_standard_languages(current_lang, { includeNoDialog: true });
    const hit = list.find((l) => l.code === code);
    return hit ? hit.language : String(code);
};

Utils.sorted_all_languages = (current_lang) => {
    return Utils._sort_languages(iso639.get_all(), current_lang);
}

/**
 * Visible text from a translatable HTML/plain value.
 * Treats CKEditor blank markup (`<p>&nbsp;</p>`, etc.) as empty.
 */
Utils.translatable_visible_text = (value) => {
    if (value == null)
        return '';
    if (typeof value !== 'string')
        return String(value);

    return striptags(value)
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#160;/gi, ' ')
        .replace(/&#x0*a0;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

Utils.cleanTranslatableProperty = (object, property) => {
    if (!(property in object))
        return object;

    if (!_.isObject(object[property]))
        return object;

    const allowedLangs = Utils.langs();
    if (Array.isArray(allowedLangs) && allowedLangs.length) {
        const allowed = new Set(allowedLangs);
        // TranslatableField with `original` (e.g. screenings) uses title.original, etc.
        allowed.add('original');
        for (const lang of Object.keys(object[property])) {
            if (!allowed.has(lang)) {
                delete object[property][lang];
            }
        }
    }

    for (const lang of Object.keys(object[property])) {
        if (!Utils.translatable_visible_text(object[property][lang]))
            delete object[property][lang];
    }

    return object;
}

Utils.trimProperties = (data) => {
    if (_.isString(data)) {
        return _.trim(data);
    };

    if (_.isObject(data)) {
        if (_.isNull(data) || _.isFunction(data)) {
            return data;
        };

        if (_.isArray(data)) {
            return data.map(e => {
                return Utils.trimProperties(e);
            });
        };

        Object.keys(data).forEach(key => {
            data[key] = Utils.trimProperties(data[key]);
        });
    };

    return data;
};

Utils.getSectionFromPath = path => {
    const paths = {
        '^/admin': Utils.SECTION_ADMIN,
        '^/devices': Utils.SECTION_ADMIN,
        '^/editions': Utils.SECTION_ADMIN,
        '^/equipment_catalog': Utils.SECTION_ADMIN,
        '^/equipment_planning': Utils.SECTION_ADMIN,
        '^/equipment_orders': Utils.SECTION_ADMIN,
        '^/settings/edit/default': Utils.SECTION_ADMIN,
        '^/cron_tasks': Utils.SECTION_ADMIN,
        '^/programmation_groups': Utils.SECTION_ADMIN,
        '^/events': Utils.SECTION_ADMIN,
        '^/sections': Utils.SECTION_ADMIN,
        '^/distributors': Utils.SECTION_ADMIN,
        '^/places': Utils.SECTION_ADMIN,
        '^/articles': Utils.SECTION_ADMIN,
        '^/articlecategories': Utils.SECTION_ADMIN,
        '^/tickettypes': Utils.SECTION_ADMIN,
        '^/layouts': Utils.SECTION_ADMIN,
        '^/email_layouts': Utils.SECTION_ADMIN,
        '^/pricinglists': Utils.SECTION_ADMIN,
        '^/bucketlists': Utils.SECTION_ADMIN,
        '^/salepoints': Utils.SECTION_ADMIN,
        '^/phases': Utils.SECTION_ADMIN,
        '^/periods': Utils.SECTION_ADMIN,
        '^/sectors': Utils.SECTION_ADMIN,
        '^/form_layouts': Utils.SECTION_ADMIN,
        '^/application_types': Utils.SECTION_ADMIN,
        '^/workflows': Utils.SECTION_ADMIN,
        '^/personnal_fields': Utils.SECTION_ADMIN,
        '^/configuration_qr_codes': Utils.SECTION_ADMIN,
        '^/cashregisters': Utils.SECTION_ADMIN,
        '^/users': Utils.SECTION_ADMIN,
        '^/ticketack_users': Utils.SECTION_ADMIN,
        '^/activities': Utils.SECTION_ADMIN,
        '^/resources': Utils.SECTION_ADMIN,
        '^/posts': Utils.SECTION_ADMIN,
        '^/post_categories': Utils.SECTION_ADMIN,
        '^/channels': Utils.SECTION_ADMIN,
        '^/operation': Utils.SECTION_OPERATION,
        '^/saleslist': Utils.SECTION_OPERATION,
        '^/bookingstatus': Utils.SECTION_OPERATION,
        '^/reservations': Utils.SECTION_OPERATION,
        '^/tickets': Utils.SECTION_OPERATION,
        '^/customers': Utils.SECTION_OPERATION,
        '^/messages': Utils.SECTION_OPERATION,
        '^/tasks': Utils.SECTION_OPERATION,
        '^/screenings': Utils.SECTION_OPERATION,
        '^/plan': Utils.SECTION_OPERATION,
        '^/applications': Utils.SECTION_OPERATION,
        '^/analysis': Utils.SECTION_ANALYSIS,
        '^/stats': Utils.SECTION_ANALYSIS,
        '^/bookingsurvey': Utils.SECTION_ANALYSIS,
    };

    let section = Utils.SECTION_OPERATION;
    Object.keys(paths).forEach(pattern => {
        if (path.match(new RegExp(pattern))) {
            section = paths[pattern];
        }
    });

    return section;
};

Utils.getMongoResources = () => {
    return Utils.MONGO_RESSOURCES;
}

Utils.isMongoAbilityModule = (module) => {
    return Utils.getMongoResources().includes(module);
}

Utils.getAppModules = () => {
    return Utils.INSTANCE_MODULES;
}

Utils.getTopbarModules = (topBarButton) => {
    return Utils.TOPBAR_MODULES[topBarButton];
}

Utils.getCurrentModules = (modules) => {
    modules.push('base_modules');
    const currentModules = [];
    const appModules = Utils.getAppModules();
    (modules || []).forEach(m => {
        if (appModules[m])
            appModules[m].forEach(p => currentModules.push(p));
    })
    return _.uniq(currentModules)
};

Utils.getAllModules = () => {
    const allModules = [];
    const appModules = Utils.getAppModules();
    Object.keys(appModules).forEach(k => {
        appModules[k].forEach(o =>
            allModules.push(o)
        )
    })
    return _.uniq(allModules)
}

/** Pad ability matrix rows to 7 columns (create + read/edit/delete self/other + actions). */
Utils.padAbilityMatrixActions = (actions) => {
    const row = [...actions];
    while (row.length < 7) {
        row.push('pad' + row.length);
    }
    return row;
};

/** Row labels aligned with admin navigation (i18n key + optional options). */
Utils.ABILITY_MATRIX_MODULE_LABELS = {
    articles: { key: 'app.articles_and_discount' },
    articlecategories: { key: 'app.categories' },
    tickettypes: { key: 'common.tickettype', options: { count: 42 } },
    pricinglists: { key: 'common.pricinglist', options: { count: 42 } },
    bucketlists: { key: 'common.bucketlist', options: { count: 42 } },
    pdf_layouts: { key: 'app.layouts' },
    email_layouts: { key: 'app.email_layouts' },
    salepoints: { key: 'common.salepoint', options: { count: 42 } },
    cashregisters: { key: 'common.cashregister', options: { count: 42 } },
    screenings: { key: 'common.screening', options: { count: 42 } },
    customers: { key: 'common.customer', options: { count: 42 } },
    tickets: { key: 'app.tickets' },
    resources: { key: 'app.resources_keys' },
    activities: { key: 'app.activities' },
    workflow_runs: { key: 'app.workflow_runs' },
    tktInstances: { key: 'common.tktInstance', options: { count: 42 } },
};

Utils.ABILITY_MATRIX_MODULE_HELPERS = {
    events: 'abilities.events_rights_hint',
    screenings: 'abilities.screenings_rights_hint',
    workflows: 'abilities.workflows_rights_hint',
    workflow_runs: 'abilities.workflow_runs_rights_hint',
    editions: 'abilities.editions_rights_hint',
    devices: 'abilities.devices_rights_hint',
    configuration_qr_codes: 'abilities.configuration_qr_codes_rights_hint',
    equipment_orders: 'abilities.equipment_orders_rights_hint',
};

/**
 * Ability matrix blocks: order and grouping aligned with Kronos admin navigation.
 * columnMode "mongo": global rights (readOther/editOther/deleteOther), cells span both self/other columns.
 * columnMode "readOnly": readOther only, centered under the read group.
 */
Utils.getAbilityMatrixBlocks = () => [
    {
        titleKey: 'app.events',
        columnMode: 'full',
        modules: [
            'programmations',
            'screenings',
            'events',
            'sections',
            'distributors',
            'places',
        ],
    },
    {
        titleKey: 'app.publishing',
        columnMode: 'full',
        modules: ['posts', 'post_categories', 'channels'],
    },
    {
        titleKey: 'app.teams',
        columnMode: 'full',
        modules: [
            'ticketack_users',
            'activities',
            'resources',
            'messages',
            'plan',
            'applications',
            'users', // extra header + collaborator rights row
            'tasks', // last: extra header + shift rights row
        ],
    },
    {
        titleKey: 'app.company',
        columnMode: 'full',
        modules: [
            'editions',
            'phases',
            'periods',
            'sectors',
            'application_types',
            'form_layouts',
            'workflows',
            'workflow_runs',
        ],
    },
    {
        titleKey: 'app.templates_and_media',
        columnMode: 'mongo',
        modules: ['pdf_layouts', 'email_layouts'],
    },
    {
        titleKey: 'app.setting_and_hardware',
        // All rows use global or single-scope rights (mongo / readOnly / editOnly); no self/other split.
        columnMode: 'mongo',
        modules: [
            'cron_tasks',
            'equipment_orders',
            'devices',
            'configuration_qr_codes',
            'tktInstances',
            'settings',
        ],
    },
    {
        titleKey: 'app.sale',
        columnMode: 'mongo',
        modules: [
            'articles',
            'articlecategories',
            'tickettypes',
            'pricinglists',
            'bucketlists',
        ],
    },
    {
        titleKey: 'abilities.block_points_of_sale',
        columnMode: 'mongo',
        modules: ['salepoints', 'cashregisters'],
    },
    {
        titleKey: 'app.tickets_clients',
        columnMode: 'mongo',
        modules: ['customers', 'tickets'],
    },
    {
        titleKey: 'app.stats',
        columnMode: 'readOnly',
        modules: ['stats', 'logs'],
    },
];

Utils.getAbilityMatrixModuleLabel = (module) => {
    return Utils.ABILITY_MATRIX_MODULE_LABELS[module] ?? { key: 'app.' + module };
};

Utils.getAbilityMatrixModuleHelper = (module) => {
    return Utils.ABILITY_MATRIX_MODULE_HELPERS[module] ?? null;
};

Utils.isAbilityMatrixModuleVisible = (module, enabled_modules) => {
    if (enabled_modules.includes(module)) {
        return true;
    }
    return module === 'workflow_runs' && enabled_modules.includes('workflows');
};

/** Module keys that can appear in the abilities matrix for an instance. */
Utils.getAbilityMatrixModuleKeysForInstance = (enabled_modules) => {
    const keys = [];
    Utils.getAbilityMatrixBlocks().forEach((block) => {
        block.modules.forEach((module_key) => {
            if (!Utils.isAbilityMatrixModuleVisible(module_key, enabled_modules)) {
                return;
            }
            if (!Utils.getAbilityMatrixActions(module_key)) {
                return;
            }
            keys.push(module_key);
        });
    });
    return _.uniq(keys);
};

/** Whether an action checkbox exists in the canonical abilities schema for a module. */
Utils.abilityModuleHasAction = (module_key, action) => {
    if (!action || !module_key) {
        return false;
    }
    const schema_module = Utils.generateAbilities()[module_key];
    if (!schema_module || typeof schema_module !== 'object') {
        return false;
    }
    return Object.prototype.hasOwnProperty.call(schema_module, action);
};

/**
 * Merge stored abilities with the matrix schema (defaults false).
 * When enabled_modules is set, only matrix-visible modules are included (+ superadmin).
 */
Utils.mergeAbilitiesWithSchema = (stored_abilities, enabled_modules = null) => {
    const schema = Utils.generateAbilities();
    const stored = stored_abilities || {};
    const module_keys = enabled_modules
        ? Utils.getAbilityMatrixModuleKeysForInstance(enabled_modules)
        : Object.keys(schema);
    const merged = {};

    module_keys.forEach((module_key) => {
        const default_module = schema[module_key];
        const stored_module = stored[module_key];
        if (default_module && typeof default_module === 'object') {
            merged[module_key] = {
                ...default_module,
                ...(stored_module && typeof stored_module === 'object' ? stored_module : {}),
            };
        } else if (default_module !== undefined) {
            merged[module_key] = stored_module ?? default_module;
        }
    });

    if (Object.prototype.hasOwnProperty.call(stored, 'superadmin')) {
        merged.superadmin = stored.superadmin;
    }

    return merged;
};

/** True when at least one visible row uses separate readSelf/readOther (etc.) checkboxes. */
Utils.abilityMatrixBlockNeedsSelfOtherHeader = (block, enabled_modules) => {
    return block.modules.some((module_key) => {
        if (!Utils.isAbilityMatrixModuleVisible(module_key, enabled_modules)) {
            return false;
        }
        if (!Utils.getAbilityMatrixActions(module_key)) {
            return false;
        }
        return Utils.getAbilityMatrixRowCellLayout(module_key, block.columnMode) === 'full';
    });
};

/** Cell layout for ability matrix rows (action + colspan over self/other pairs). */
Utils.ABILITY_MATRIX_ROW_LAYOUTS = {
    full: null,
    mongo: [
        { action: 'create', col_span: 1 },
        { action: 'readOther', col_span: 2 },
        { action: 'editOther', col_span: 2 },
        { action: 'deleteOther', col_span: 2 },
    ],
    mongoNoCreate: [
        { action: null, col_span: 1 },
        { action: 'readOther', col_span: 2 },
        { action: 'editOther', col_span: 2 },
        { action: 'deleteOther', col_span: 2 },
    ],
    readOnly: [
        { action: null, col_span: 1 },
        { action: 'readOther', col_span: 2 },
        { action: null, col_span: 2 },
        { action: null, col_span: 2 },
    ],
    editOnly: [
        { action: null, col_span: 1 },
        { action: null, col_span: 2 },
        { action: 'editSelf', col_span: 2 },
        { action: null, col_span: 2 },
    ],
    editOnlyOther: [
        { action: null, col_span: 1 },
        { action: null, col_span: 2 },
        { action: 'editOther', col_span: 2 },
        { action: null, col_span: 2 },
    ],
};

/**
 * Per-row column layout (overrides block default for mixed blocks).
 * @returns {keyof Utils.ABILITY_MATRIX_ROW_LAYOUTS}
 */
Utils.getAbilityMatrixRowCellLayout = (module, block_column_mode = 'full') => {
    if (module === 'configuration_qr_codes' || module === 'stats' || module === 'logs') {
        return 'readOnly';
    }
    if (module === 'devices' || module === 'workflow_runs') {
        return 'mongoNoCreate';
    }
    if (module === 'equipment_orders') {
        return 'mongo';
    }
    if (module === 'settings' || module === 'tktInstances') {
        return 'editOnly';
    }
    if (block_column_mode === 'mongo' || block_column_mode === 'readOnly') {
        return block_column_mode;
    }
    if (Utils.getMongoResources().includes(module)) {
        return 'mongo';
    }
    return 'full';
};

/**
 * Actions shown per module in TktInstanceAbilitiesTemplate (null = hidden row).
 * Keys use the same names as stored abilities JSON.
 * @param {string} module
 */
Utils.getAbilityMatrixActions = (module) => {
    if (module === 'equipment_catalog' || module === 'instance_equipment_config') {
        return null;
    }
    if (module === 'settings') {
        return Utils.padAbilityMatrixActions(['pad0', 'pad1', 'pad2', 'editSelf', 'pad4', 'pad5', 'pad6']);
    }
    if (module === 'stats' || module === 'logs') {
        return Utils.padAbilityMatrixActions(['pad0', 'pad1', 'readOther', 'pad3', 'pad4', 'pad5', 'pad6']);
    }
    if (module === 'messages') {
        return Utils.padAbilityMatrixActions(['create', 'pad1', 'pad2', 'pad3', 'pad4', 'pad5', 'pad6']);
    }
    if (module === 'tktInstances') {
        return Utils.padAbilityMatrixActions(['pad0', 'pad1', 'pad2', 'editSelf', 'pad4', 'pad5', 'pad6']);
    }
    if (module === 'configuration_qr_codes') {
        return Utils.padAbilityMatrixActions(['pad0', 'pad1', 'readOther', 'pad3', 'pad4', 'pad5', 'pad6']);
    }
    if (module === 'devices' || module === 'workflow_runs') {
        return Utils.padAbilityMatrixActions(['pad0', 'pad1', 'readOther', 'pad3', 'editOther', 'pad5', 'deleteOther']);
    }
    if (module === 'equipment_orders') {
        return Utils.padAbilityMatrixActions(['create', 'pad1', 'readOther', 'pad3', 'editOther', 'pad5', 'deleteOther']);
    }
    if (Utils.getMongoResources().includes(module)) {
        return Utils.padAbilityMatrixActions(['create', 'pad1', 'readOther', 'pad3', 'editOther', 'pad5', 'deleteOther']);
    }
    return Utils.padAbilityMatrixActions([
        'create', 'readSelf', 'readOther', 'editSelf', 'editOther', 'deleteSelf', 'deleteOther'
    ]);
};

Utils.generateAbilities = (abilityName) => {
    let bool = false;
    let abilities = {}
    Utils.getAllModules().forEach(mod => {
        if (mod === "equipment_catalog") {
            return;
        }
        if (mod === "stats")
            abilities["stats"] = { readOther: bool }
        else if (mod === "logs")
            abilities["logs"] = { readOther: bool }
        else if (mod === "messages")
            abilities["messages"] = { create: bool }
        else if (mod === "tasks")
            abilities["tasks"] = {
                create: bool,
                readSelf: bool,
                readOther: bool,
                editSelf: bool,
                editOther: bool,
                deleteSelf: bool,
                deleteOther: bool,
                assign: bool,
                change_status: bool,
                claim_for_others: bool,
                renounce_for_others: bool,
                claim: bool,
                renounce: bool
            }
        else if (mod === "settings")
            abilities["settings"] = {
                editSelf: bool,
            }
        else if (mod === "tktInstances")
            abilities["tktInstances"] = {
                editSelf: bool,
            }
        else if (mod === "users")
            abilities["users"] = {
                create: bool,
                readSelf: bool,
                readOther: bool,
                editSelf: bool,
                editOther: bool,
                deleteSelf: bool,
                deleteOther: bool,
                pick_activities: bool,
                read_abilities: bool,
                edit_abilities: bool,
                edit_templates: bool,
                spoof: bool,
            }
        else if (mod === "equipment_orders")
            abilities["equipment_orders"] = {
                create: bool,
                readOther: bool,
                editOther: bool,
                deleteOther: bool,
            }
        else if (mod === "devices")
            abilities["devices"] = {
                readOther: bool,
                editOther: bool,
                deleteOther: bool,
            }
        else if (mod === "workflow_runs")
            abilities["workflow_runs"] = {
                readOther: bool,
                editOther: bool,
                deleteOther: bool,
            }
        else if (mod === "configuration_qr_codes")
            abilities["configuration_qr_codes"] = {
                readOther: bool,
            }
        else if (mod === "instance_equipment_config") {
            return;
        }
        else if (Utils.getMongoResources().includes(mod)) {
            abilities[`${mod}`] = {
                create: bool,
                readOther: bool,
                editOther: bool,
                deleteOther: bool,
            }
        }
        else {
            abilities[`${mod}`] = {
                create: bool,
                readSelf: bool,
                readOther: bool,
                editSelf: bool,
                editOther: bool,
                deleteSelf: bool,
                deleteOther: bool,
            }
        }
    });

    //When you create an ability template (with name)
    if (abilityName) {
        let template = {}
        template["name"] = abilityName
        template["abilities"] = abilities
        return template
    }

    //when you edit an user and he has no abilities yet: we generate the empty object
    return abilities;
};

Utils.getSettingsOverridablePaths = () => {
    return [
        { icon: 'vcard', key: 'customer.name' },
        { icon: 'vcard', key: 'customer.name_short' },
        { icon: 'vcard', key: 'customer.phone' },
        { icon: 'vcard', key: 'customer.address' },
        { icon: 'vcard', key: 'customer.tax.number' },
        { icon: 'vcard', key: 'customer.email.mail' },
        { icon: 'vcard', key: 'customer.email.bcc' },
        { icon: 'vcard', key: 'customer.email.errors_to' },
        { icon: 'vcard', key: 'customer.email.override_to' },
        { icon: 'photo', key: 'images' },
        { icon: 'credit-card', key: 'payment.methods' },
        { icon: 'credit-card', key: 'pos' },
        { icon: 'globe', key: 'eshop' },
        { icon: 'file-pdf-o', key: 'pdf_layouts' },
        { icon: 'envelope', key: 'email_layouts' },
        { icon: 'external-link', key: 'integrations.procinema' },
    ];
}

Utils.sortOnProperty = (property) => (a, b) => {
    a = property ? a[property] : a;
    b = property ? b[property] : b;

    // force null and undefined to the bottom
    a = a === null || a === undefined ? '' : a
    b = b === null || b === undefined ? '' : b
    // force any string values to lowercase
    a = typeof a === 'string' ? a.toLowerCase() : a
    b = typeof b === 'string' ? b.toLowerCase() : b
    // Return either 1 or -1 to indicate a sort priority
    if (a > b || (a === 0 && b === "")) {
        return 1
    }
    if (a < b || (b === 0 && a === "")) {
        return -1
    }
    // returning 0, undefined or any falsey value will use subsequent sorts or
    // the index as a tiebreaker
    return 0
}

/**
 * react-table-6 calls defaultSortMethod with (valueA, valueB) for the column being sorted,
 * not (row, row). {@link Utils.sortOnProperty}('x') is therefore wrong for string columns
 * (e.g. "movie" title) because it re-applies [property] to the cell.
 *
 * Handles stats cells: string titles, `''` (no data), or buckets `{ total, nbPaid, ... }` (sort by .total number).
 * @param {*} a
 * @param {*} b
 * @returns {number}
 */
Utils.compareStatsTableCellValues = (a, b) => {
    const isEmpty = (v) => v == null || v === '' || (typeof v === 'number' && Number.isNaN(v));
    if (isEmpty(a) && isEmpty(b))
        return 0;
    if (isEmpty(a))
        return 1;
    if (isEmpty(b))
        return -1;
    if (typeof a === 'string' && typeof b === 'string')
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    if (a && b && typeof a === 'object' && typeof b === 'object' && 'total' in a && 'total' in b) {
        const an = typeof a.total === 'number' ? a.total : NaN;
        const bn = typeof b.total === 'number' ? b.total : NaN;
        if (Number.isFinite(an) && Number.isFinite(bn)) {
            if (an < bn)
                return -1;
            if (an > bn)
                return 1;
            return 0;
        }
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Alphabetical compare of the string shown in a list.
 * sensitivity "base" folds accents so French É/E sort together.
 */
Utils.compareDisplayedText = (a, b, locale) => {
    return String(a ?? '').localeCompare(String(b ?? ''), locale || undefined, { sensitivity: 'base' });
};

Utils.sort_object_keys = (obj) => {
    return Object.keys(obj)
        .sort()
        .reduce((accumulator, key) => {
            accumulator[key] = obj[key];

            return accumulator;
        }, {});
}

Utils.integrationParams = {
    "cinech": {
        "cinema_ids": "",
        "url": "",
        "import": {
            "screenings": {
                "enabled": false,
                "delete_future": false,
                "delete_existing": false
            }
        }
    },
    "gtm" : {
        "id": ""
    },
    "posthog": {
        "project_api_key": "",
        "personal_api_key": "",
        "project_id": "",
        "host": ""
    },
    "plausible": {
        "enabled": true,
        "host": "https://analytics.netoxygen.ch"
    },
    "eventival": {
        "name": "",
        "year": "",
        "api_key": "",
        "import": {
          "screenings": {
            "enabled": false,
            "fullfill_people": false,
            "delete_future": false,
            "delete_existin": false
            }
        }
    },
    "fiona": {
        "name": "",
        "edition_id": "",
        "api_key": "",
        "import": {
            "screenings": {
            "enabled": true,
            "delete_future": true,
            "delete_existing": true
            }
        }
    },
    "kronos": {
        "api_key": "",
        "import": {
          "screenings": {
            "enabled": false,
            "delete_future": false,
            "delete_existing": false
          }
        }
    },
    "json": {
        "url": "",
        "import": {
          "screenings": {
            "enabled": false,
            "delete_future": false,
            "delete_existing": false
          }
        }
    },
    "ozu": {
        "url": "",
        "import": {
            "screenings": {
                "enabled": false,
                "delete_future": false,
                "delete_existing": false
            }
        }
    },
    "maccsbox": {
        "ftp": {
          "hostname": "",
          "password": "",
          "pasv": true,
          "directory": "",
          "username": ""
        }
    },
    "mailchimp": {
        "newsletter": {
            "api_key": "",
            "list_id": "",
            "interest": "",
            "tags": [],
            "merge_fields": {
                "country": "",
                "zip": "",
                "city": "",
                "address": ""
            }
        },
        "survey": {
            "api_key": "",
            "list_id": "",
            "interest": "",
            "tags": [],
            "merge_fields": {
                "country": "",
                "zip": "",
                "city": "",
                "address": ""
            }
        }
    },
    "brevo": {
        "newsletter": {
            "api_key": "",
            "list_id": "",
            "tags": [],
            "attributes": {
                "country": "",
                "zip": "",
                "city": "",
                "address": ""
            }
        },
        "survey": {
            "api_key": "",
            "list_id": "",
            "tags": [],
            "attributes": {
                "country": "",
                "zip": "",
                "city": "",
                "address": ""
            }
        }
    },
    "getresponse": {
        "newsletter": {
            "api_key": "",
            "campaign_id": "",
            "tags": [],
            "custom_fields": {
                "country": "",
                "zip": "",
                "city": "",
                "address": "",
                "language": ""
            }
        },
        "survey": {
            "api_key": "",
            "campaign_id": "",
            "tags": [],
            "custom_fields": {
                "country": "",
                "zip": "",
                "city": "",
                "address": "",
                "language": ""
            }
        }
    },
    "own_it": {
        "api_key": "",
        "places_ids": [],
        "test": true,
        "codes_creation": {
            "product_id": "",
            "player_ids": "",
            "tickettypes": [],
            "valid_from": "",
            "valid_until": ""
        }
    },
    "recaptcha": {
        "public_key": "",
        "secret_key": ""
    },
    "shift72": {
        "endpoint": "",
        "api_key": ""
    },
    "pantaflix": {
        "places_ids": [],
        "provider": "",
        "content_id_prefix": ""
    },
    "como": {
        "api_key": "",
        "branch_id": ""
    },
    "procinema": {
        "api_key": "",
        "cinema_site_id": "",
        "environment": "sandbox"
    },
    "weserv": {
        "proxy_img_host": "images.weserv.nl",
    },
    "wp": {
        "websites": [
          {
            "name": "",
            "rate": 1,
            "uri": ""
          }
        ]
    }
};

Utils.randomString = length => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    let result  = '';
    let counter = 0;
    const charactersLength = characters.length;
    while (counter < length) {
        result += characters.charAt(Math.floor(
            Math.random() * charactersLength
        ));
        counter += 1;
    }

    return result;
}

Utils.joinWithComma = (strings, t) => {
    if (!strings?.length)
        return null;

    if (strings.length === 1)
        return strings[0];

    const first = strings.slice(0, -1);
    const last  = strings.slice(-1);

    return [
        first.join(', '),
        last
    ].join(` ${t('common.and')} `);
}

/**
 * Join display names, dropping consecutive/duplicate parts (case-insensitive).
 * "Aigle" + "Aigle" → "Aigle"; "Sion" + "Caisse 2" → "Sion - Caisse 2".
 */
Utils.join_distinct = (parts, separator = ' - ') => {
    const unique = [];
    const seen = new Set();

    (parts || []).forEach((part) => {
        const text = part == null ? '' : String(part).trim();
        if (!text)
            return;

        const key = text.toLocaleLowerCase();
        if (seen.has(key))
            return;

        seen.add(key);
        unique.push(text);
    });

    return unique.length ? unique.join(separator) : null;
}

/**
 * Get an obj property by its dotted path
 *
 * @param {Object} obj
 * @param {String} path
 *
 * @return {String?}
 */
Utils.getByPath = (obj, path) => {
    return (path || '').split('.').reduce((acc, key) => {
        if (key === '?' && Object.keys(acc)?.length > 0)
            key = Object.keys(acc)[0];
        return acc?.[key];
    }, obj || {});
}

/**
 * Format an edition number as a localized ordinal (31e, 31st, 31., 31°).
 *
 * @param {number|string|null|undefined} number
 * @param {string|null|undefined} lang
 * @returns {string}
 */
Utils.formatEditionOrdinal = (number, lang) => {
    const n = Number(number);
    if (!Number.isInteger(n) || n < 1)
        return '';

    const locale = String(lang || 'fr').slice(0, 2).toLowerCase();

    if (locale === 'en') {
        const mod100 = n % 100;
        const mod10 = n % 10;
        let suffix = 'th';
        if (mod100 < 11 || mod100 > 13) {
            if (mod10 === 1)
                suffix = 'st';
            else if (mod10 === 2)
                suffix = 'nd';
            else if (mod10 === 3)
                suffix = 'rd';
        }
        return `${n}${suffix}`;
    }

    if (locale === 'de')
        return `${n}.`;

    if (locale === 'it')
        return `${n}°`;

    if (n === 1)
        return '1er';
    return `${n}e`;
};

/**
 * Replace shortcodes found in the provided text
 * by the values extracted from the provided context
 */
Utils.replaceShortcodes = (text, context) => {
    const match = [...text.matchAll(/\[(?:(?<resource>[^.\]]+)(?:\.))?((?<path>[^\]]+))\]/gm)];
    if (!match?.length)
        return text;

    // Longer Moment tokens first so `_dddd` is not partially matched as `_d`.
    // Compact aliases (no spaces in path): DMMMM → D MMMM, MMMMD → MMMM D, …
    // Optional locale override after the format: `_LL.en` (same idea as multilingual fields).
    const format_aliases = {
        DMMMM: 'D MMMM',
        DMMM: 'D MMM',
        MMMMD: 'MMMM D',
        MMMD: 'MMM D',
        'D.MMMM': 'D. MMMM',
        'D.MMM': 'D. MMM',
    };
    const formats = [
        'DDDo', 'dddd', 'ddd', 'dd', 'do', 'd', 'YYYY', 'HH', 'mm',
        'DMMMM', 'DMMM', 'MMMMD', 'MMMD', 'D.MMMM', 'D.MMM',
        'LLLL', 'llll', 'LLL', 'lll', 'LTS', 'LT', 'LL', 'll', 'L', 'I',
    ];
    const date_locales = ['fr', 'en', 'de', 'it'];

    match.forEach(m => {
        const full = m[0];
        const { resource } = m.groups;
        let pathStr = m.groups.path;
        const source = (resource ? context[resource] : context) || {};

        // Date format / day-offset / locale, e.g. event_start_date-7_LL.en
        let dateFormat = null;
        let locale = null;
        formats.forEach(format => {
            if (dateFormat)
                return;

            for (let i = 0; i < date_locales.length; i++) {
                const loc = date_locales[i];
                const with_locale = `_${format}.${loc}`;
                if (pathStr.length >= with_locale.length
                    && pathStr.slice(-with_locale.length).toLowerCase() === with_locale.toLowerCase()) {
                    pathStr = pathStr.slice(0, -with_locale.length);
                    dateFormat = format_aliases[format] || format;
                    locale = loc;
                    return;
                }
            }

            if (pathStr.endsWith('_' + format)) {
                pathStr = pathStr.slice(0, -(format.length + 1));
                dateFormat = format_aliases[format] || format;
            }
        });

        let dayOffset = null;
        if (dateFormat) {
            const offsetMatch = pathStr.match(/^(.+)([+-]\d+)$/);
            if (offsetMatch) {
                pathStr = offsetMatch[1];
                dayOffset = parseInt(offsetMatch[2], 10);
            }
        }

        // Only strip `.en` for ordinal (not customer.name.fr — that stays on path).
        if (!dateFormat && !locale) {
            const locale_match = pathStr.match(/^(.+)\.(fr|en|de|it)$/i);
            if (locale_match && locale_match[1] === 'ordinal') {
                pathStr = locale_match[1];
                locale = locale_match[2].toLowerCase();
            }
        }

        const format_lang = locale || context.lang;

        let value;
        if (resource === 'edition' && pathStr === 'ordinal') {
            value = Utils.formatEditionOrdinal(source.number, format_lang);
        } else {
            value = Utils.getByPath(source, pathStr);
        }

        if (dateFormat) {
            if (value === null || value === undefined || value === '') {
                value = '';
            } else {
                value = moment(value);
                if (dayOffset != null)
                    value = value?.add(dayOffset, 'days');
                if (format_lang)
                    value = value?.locale(format_lang);
                value = value?.format(dateFormat);
            }
        }
        text = text.replace(full, value ?? '');
    });

    return text;
}

Utils.is_maccsbox_integration_enabled = function (setting) {
    return !!(setting && setting.integrations && setting.integrations.maccsbox);
};

/**
 * In-app path to the movie “details” tab (SUISA, original language, etc.) for a programmation event row.
 *
 * @param {{ type?: string, id?: string }} ev Sub-event from programmation.events (redux-form value)
 * @returns {string|null}
 */
Utils.programmation_movie_details_edit_path = function (ev) {
    if (!ev || ev.type !== Utils.EVENT_TYPE_MOVIE) {
        return null;
    }
    const id = ev.id;
    if (!id) {
        return null;
    }
    return `/events/${Utils.EVENT_TYPE_MOVIE}/edit/${id}/details`;
};

/**
 * Sanitize in-app path from router location.state (avoid open redirects).
 *
 * @param {unknown} path
 * @returns {string|null}
 */
Utils.safe_spa_internal_path = function (path) {
    if (typeof path !== 'string' || path.length === 0) {
        return null;
    }
    if (!path.startsWith('/') || path.startsWith('//')) {
        return null;
    }
    if (path.includes('://')) {
        return null;
    }
    return path;
};

/**
 * Whether Maccsbox rules apply to shared events (audio, original language, SUISA).
 * Relaxed when integration is off, when a single programmation ignores Maccsbox,
 * when every sub-programmation in a group ignores Maccsbox, or when a group has
 * no sub-programmations yet (create used to hit this before the first slot was
 * seeded — empty groups intentionally skip Maccsbox until a slot exists).
 *
 * @param {{ events?: Array, options?: object, programmations?: Array }} values redux-form values
 * @param {*} setting
 */
Utils.should_enforce_maccsbox_on_events = function (values, setting) {
    if (!Utils.is_maccsbox_integration_enabled(setting)) {
        return false;
    }
    const progs = values?.programmations;
    if (Array.isArray(progs)) {
        if (progs.length === 0) {
            return false;
        }
        const allIgnore = progs.every((p) => {
            if (p?.override_defaults) {
                return !!p?.options?.ignore_on_maccsbox;
            }
            return !!values?.options?.ignore_on_maccsbox;
        });
        return !allIgnore;
    }
    if (values?.options?.ignore_on_maccsbox) {
        return false;
    }
    return true;
};

/**
 * Programmation / programmation-group redux-form errors when Maccsbox is enabled.
 *
 * @param {{ events?: Array, options?: object, programmations?: Array }} values
 * @param {*} setting Default settings document
 * @param {function(string): string} t i18n t()
 */
Utils.validate_programmation_maccsbox_events = function (values, setting, t) {
    if (!Utils.should_enforce_maccsbox_on_events(values, setting)) {
        return {};
    }
    const errors = {};
    const list = values?.events;
    if (Array.isArray(list) && list.length) {
        const eventsErrors = [];
        list.forEach((ev, i) => {
            if (ev == null || Utils.is_programmation_pause(ev)) {
                return;
            }
            const row = {};
            if (ev.audio === '' || ev.audio == null) {
                row.audio = t('programmations.maccsbox_diffusion_audio_required');
            }
            if (ev.type === Utils.EVENT_TYPE_MOVIE) {
                if (ev.original_language === '' || ev.original_language == null) {
                    row.original_language = t('programmations.maccsbox_original_audio_required');
                }
                if (String(ev.suisa ?? '').trim() === '') {
                    row.suisa = t('programmations.maccsbox_suisa_required');
                }
            }
            if (Object.keys(row).length) {
                eventsErrors[i] = row;
            }
        });
        if (eventsErrors.length) {
            errors.events = eventsErrors;
        }
    }

    if (Array.isArray(values?.programmations)) {
        const slotEventErrors = [];
        values.programmations.forEach((p, pi) => {
            if (!p?.override_defaults || !Array.isArray(p.events) || p?.options?.ignore_on_maccsbox) {
                return;
            }
            const rowErrors = [];
            p.events.forEach((ev, i) => {
                if (ev == null || Utils.is_programmation_pause(ev)) {
                    return;
                }
                const row = {};
                if (ev.audio === '' || ev.audio == null) {
                    row.audio = t('programmations.maccsbox_diffusion_audio_required');
                }
                if (Object.keys(row).length) {
                    rowErrors[i] = row;
                }
            });
            if (rowErrors.length) {
                slotEventErrors[pi] = { events: rowErrors };
            }
        });
        if (slotEventErrors.length) {
            errors.programmations = slotEventErrors;
        }
    }

    return errors;
};

Utils.programmation_has_times = function (times) {
    return Array.isArray(times) && times.some((time) => String(time ?? '').trim() !== '');
};

Utils.programmation_has_days = function (days) {
    return Array.isArray(days) && days.length > 0;
};

Utils.programmation_has_place = function (place_id) {
    return place_id != null && String(place_id).trim() !== '';
};

/**
 * Require at least one show time: group slots (`programmations[].times`) or a
 * standalone programmation (`times`).
 *
 * @param {{ times?: Array, programmations?: Array }} values
 * @param {function(string): string} [_t]
 */
Utils.validate_programmation_times = function (values, _t) {
    const errors = {};
    const message = 'programmations.times_required';
    if (Array.isArray(values?.programmations)) {
        const slotErrors = [];
        values.programmations.forEach((p, i) => {
            if (p == null)
                return;
            if (!Utils.programmation_has_times(p.times)) {
                slotErrors[i] = { times: message };
            }
        });
        if (slotErrors.length) {
            errors.programmations = slotErrors;
        }
        return errors;
    }
    if (!Utils.programmation_has_times(values?.times)) {
        errors.times = message;
    }
    return errors;
};

/**
 * Require at least one weekday: group slots (`programmations[].days`) or a
 * standalone programmation (`days`).
 *
 * @param {{ days?: Array, programmations?: Array }} values
 * @param {function(string): string} [_t]
 */
Utils.validate_programmation_days = function (values, _t) {
    const errors = {};
    const message = 'programmations.days_required';
    if (Array.isArray(values?.programmations)) {
        const slotErrors = [];
        values.programmations.forEach((p, i) => {
            if (p == null)
                return;
            if (!Utils.programmation_has_days(p.days)) {
                slotErrors[i] = { days: message };
            }
        });
        if (slotErrors.length) {
            errors.programmations = slotErrors;
        }
        return errors;
    }
    if (!Utils.programmation_has_days(values?.days)) {
        errors.days = message;
    }
    return errors;
};

/**
 * Require a room: group slots (`programmations[].place_id`) or a standalone
 * programmation (`place_id`). Empty (`---`) only happens when the instance has
 * no default / unique / last-used place to prefill.
 *
 * @param {{ place_id?: string, programmations?: Array }} values
 * @param {function(string): string} [_t]
 */
Utils.validate_programmation_places = function (values, _t) {
    const errors = {};
    const message = 'programmations.place_required';
    if (Array.isArray(values?.programmations)) {
        const slotErrors = [];
        values.programmations.forEach((p, i) => {
            if (p == null)
                return;
            if (!Utils.programmation_has_place(p.place_id)) {
                slotErrors[i] = { place_id: message };
            }
        });
        if (slotErrors.length) {
            errors.programmations = slotErrors;
        }
        return errors;
    }
    if (!Utils.programmation_has_place(values?.place_id)) {
        errors.place_id = message;
    }
    return errors;
};

function merge_programmation_error_rows(left, right) {
    if (!left)
        return right;
    if (!right)
        return left;
    const merged = { ...left, ...right };
    if (left.events || right.events) {
        merged.events = merge_programmation_error_lists(left.events, right.events);
    }
    return merged;
}

function merge_programmation_error_lists(left, right) {
    const a = left || [];
    const b = right || [];
    const len = Math.max(a.length, b.length);
    const out = [];
    for (let i = 0; i < len; i++) {
        if (a[i] || b[i])
            out[i] = merge_programmation_error_rows(a[i], b[i]);
    }
    return out;
}

Utils.merge_programmation_sync_errors = function (left, right) {
    const l = left || {};
    const r = right || {};
    const out = { ...l };
    Object.keys(r).forEach((key) => {
        if (Array.isArray(l[key]) || Array.isArray(r[key])) {
            out[key] = merge_programmation_error_lists(l[key], r[key]);
            return;
        }
        if (l[key] && r[key] && typeof l[key] === 'object' && typeof r[key] === 'object') {
            out[key] = { ...l[key], ...r[key] };
            return;
        }
        out[key] = r[key];
    });
    return out;
};

Utils.validate_programmation_form = function (values, setting, t) {
    return Utils.merge_programmation_sync_errors(
        Utils.merge_programmation_sync_errors(
            Utils.merge_programmation_sync_errors(
                Utils.validate_programmation_times(values, t),
                Utils.validate_programmation_days(values, t)
            ),
            Utils.validate_programmation_places(values, t)
        ),
        Utils.validate_programmation_maccsbox_events(values, setting, t)
    );
};

/**
 * True when the only sync errors are missing show times.
 * Combined with HoursField's committable draft so Save stays enabled while an
 * hour is being typed, but stays disabled when the field is empty.
 *
 * @param {object} errors
 * @returns {boolean}
 */
Utils.programmation_form_errors_are_only_missing_times = function (errors) {
    if (!errors) {
        return false;
    }
    const keys = Object.keys(errors).filter((key) => {
        const value = errors[key];
        if (value == null) {
            return false;
        }
        if (Array.isArray(value) && value.length === 0) {
            return false;
        }
        return true;
    });
    if (keys.length === 0) {
        return false;
    }
    if (keys.every((key) => key === 'times')) {
        return true;
    }
    if (keys.some((key) => key !== 'programmations')) {
        return false;
    }
    const slots = errors.programmations;
    if (!Array.isArray(slots) || !slots.length) {
        return false;
    }
    return slots.every((row) => {
        if (!row) {
            return true;
        }
        const rowKeys = Object.keys(row).filter((key) => row[key] != null);
        return rowKeys.length === 1 && rowKeys[0] === 'times';
    });
};

/**
 * Whether Maccsbox programmation validation returned any errors.
 * Complements redux-form's `invalid`: sync errors on paths without a registered
 * Field (e.g. events[].original_language) are not reflected in `invalid`.
 */
Utils.has_programmation_maccsbox_sync_errors = function (values, setting, t) {
    const errs = Utils.validate_programmation_maccsbox_events(values, setting, t);
    return errs != null && Object.keys(errs).length > 0;
};

/**
 * Compute the last "session" datetime for a programmation.
 * We use stop_at day and the latest configured time (if any).
 *
 * @param {{ stop_at?: any, times?: Array<string> }} programmation
 * @returns {moment.Moment|null}
 */
Utils.programmation_last_session_at = function (programmation) {
    const stopAt = programmation?.stop_at;
    if (!stopAt) {
        return null;
    }
    const stop = moment(stopAt);
    if (!stop.isValid()) {
        return null;
    }

    const times = programmation?.times;
    if (!Array.isArray(times) || times.length === 0) {
        return stop.clone().endOf('day');
    }

    // times are stored as "HH:mm" or "HH:mm-HH:mm" (overnight end wraps to the next day)
    const parsed = times
        .map((t) => {
            const token = parseProgrammationTimeToken(t);
            if (!token)
                return null;
            const [startH, startM] = token.start.split(':').map(Number);
            const startMinutes = startH * 60 + startM;
            let endMinutes = startMinutes;
            if (token.end) {
                const [endH, endM] = token.end.split(':').map(Number);
                endMinutes = endH * 60 + endM;
                if (endMinutes <= startMinutes)
                    endMinutes += 24 * 60;
            }
            return {
                hour: token.end ? Math.floor((endMinutes % (24 * 60)) / 60) : startH,
                minute: token.end ? (endMinutes % 60) : startM,
                minutesOfDay: endMinutes,
                wrapsNextDay: !!(token.end && endMinutes >= 24 * 60),
            };
        })
        .filter(Boolean);

    if (parsed.length === 0) {
        return stop.clone().endOf('day');
    }

    const lastTime = _.maxBy(parsed, 'minutesOfDay');
    const last = stop.clone().hour(lastTime.hour).minute(lastTime.minute).second(0).millisecond(0);
    if (lastTime.wrapsNextDay)
        last.add(1, 'day');
    return last;
};

/**
 * Programmation is considered completed when its last session is in the past.
 *
 * @param {*} programmation
 * @param {moment.Moment} now
 * @returns {boolean}
 */
Utils.is_programmation_completed = function (programmation, now = moment()) {
    const last = Utils.programmation_last_session_at(programmation);
    if (!last) {
        return false;
    }
    return last.isBefore(now);
};

/**
 * Compute the last session datetime for a programmation group.
 * It uses the group's stop_at day and the latest time among its programmations.
 *
 * @param {{ stop_at?: any, programmations?: Array<{ times?: Array<string> }> }} group
 * @returns {moment.Moment|null}
 */
Utils.programmation_group_last_session_at = function (group) {
    const stopAt = group?.stop_at;
    if (!stopAt) {
        return null;
    }
    const stop = moment(stopAt);
    if (!stop.isValid()) {
        return null;
    }

    const progs = group?.programmations;
    const times = Array.isArray(progs)
        ? _.uniq(_.flatten(progs.map((p) => p?.times || [])))
        : [];

    return Utils.programmation_last_session_at({ stop_at: stop, times });
};

/**
 * Programmation group is considered completed when its last session is in the past.
 *
 * @param {*} group
 * @param {moment.Moment} now
 * @returns {boolean}
 */
Utils.is_programmation_group_completed = function (group, now = moment()) {
    const last = Utils.programmation_group_last_session_at(group);
    if (!last) {
        return false;
    }
    return last.isBefore(now);
};

export default Utils;
